/**
 * Lesson Viewer Component
 * Displays lesson content: text, audio, exercises, and quiz
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Volume2,
  Lock,
  Play,
  Clock,
  Trophy,
  Image as ImageIcon,
  Video,
  Lightbulb,
  Heart,
  Loader2,
  PenLine,
  ClipboardList,
  Info,
  LayoutDashboard
} from 'lucide-react';
import { favoriteService } from '../../services/favoriteService';
import {
  contentService,
  LessonDetailDto,
  ContentBlockDto,
  ContentBlockType,
  LessonQuizDto,
  LessonQuizSubmissionDto,
  EnhancedQuizResultDto,
  UpdateLessonProgressDto,
  CourseDetailDto,
  LessonExerciseSummaryDto
} from '../../services/contentService';
import { notifyGamificationAfterAction } from '../../utils/gamificationNotif';
import { useAuth } from '../../hooks/useAuth';
import { formatRatingOneDecimal, ratingAverageFromSummary } from '../../utils/formatRating';
import { LearnerHeroViewFavoriteCluster } from '../shared/LearnerHeroViewFavoriteCluster';
import { useNotifications } from '../feedback/NotificationSystem';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  pickBlockDisplayLocale,
  pickBlockTextContent,
  pickBlockTextFormatting,
  pickBlockTips,
  pickBlockTitle,
  pickBlockStickyAudio,
} from '../../utils/localizedContent';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { LearnerRatingCommentsTrigger } from '../shared/LearnerRatingCommentsTrigger';
import { PersianOverlayLoader } from '../shared/PersianLoader';
import { AudioPlayer } from '../shared/AudioPlayer';
import { QuizModal } from './QuizModal';
import { ExerciseModal, ExerciseType } from './ExerciseModal';
import { userActionLogger } from '../../services/userActionLogger';
import type { LessonTypographyScope } from '../../constants/learnerPersianFonts';
import { useTheme } from '../../hooks/useTheme';
import { FormattedTextBlock } from './FormattedTextBlock';
import { WordHelpBlockToggle } from './WordHelpBlockToggle';
import { WordHelpProvider } from '../../contexts/WordHelpContext';
import { useStickyAudioBlockToggle, useRefreshStickyAudioMap } from '../../contexts/StickyAudioContext';
import { textBlockHasStickyAudioWords, type StickyAudioMap } from '../../utils/stickyAudio';
import { collectVocabularyItems, vocabularyToStickyMap } from '../../utils/wordHelp';
import { LearnerExpandableImage } from './LearnerExpandableImage';
import { TextAudioBlock } from './TextAudioBlock';
import {
  clearTextAudioTipPendingOpenAnimation,
  computeTipTooltipPosition,
  isAnchorOutsideView,
  dispatchPauseTextAudioForTip,
  isTextAudioTipMarkedOpen,
  isTextAudioTipPendingOpenAnimation,
  isWithinTipOpenGracePeriod,
  markTextAudioTipClosed,
  markTextAudioTipOpen,
  markTextAudioTipPendingOpenAnimation,
  setTextAudioTipPointerActive,
  TEXT_AUDIO_TIP_OPEN_GRACE_MS
} from '../../utils/textAudioTipPlayback';
import { useLessonTypographyPreferences } from '../../hooks/useLessonTypographyPreferences';
import { useVisibleBlockTracker, useBlockVisibility } from './VisibleBlockTracker';
import { CommentModal } from './CommentModal';
import { commentService } from '../../services/commentService';
import { subscriptionService } from '../../services/subscriptionService';
import { SubscriptionPlan } from '../../types/subscription';
import { SubscriptionPlanIcons } from '../shared/SubscriptionPlanIcons';
import { ExerciseLockedModal } from '../shared/ExerciseLockedModal';
import { ClickableTooltip } from '../shared/ClickableTooltip';
import { saveLastLessonPosition } from '../../utils/lastLearningPosition';
import { renderHighlightedText } from '../../utils/searchHighlight';
import { LessonJourneyTimeline } from './LessonJourneyTimeline';
import {
  areLessonJourneyPassPrerequisitesMet,
  areMandatoryInlineExercisesComplete,
  buildLessonJourneySteps,
  getLessonJourneyBlockers,
  resolveLessonQuizzes
} from '../../utils/lessonJourneySteps';
import { isReviewableLessonBlock, useLessonContentViewTracking } from '../../utils/lessonContentViewTracking';
import { LessonNotPassedModal } from './LessonNotPassedModal';
import { NonFarsiContentHintModal } from './NonFarsiContentHintModal';
import { EnglishLockedContentHintIcon } from '../shared/EnglishLockedContentHintIcon';
import { useNonFarsiContentGate } from '../../hooks/useNonFarsiContentGate';
import { isDesignedForNonFarsiUsers } from '../../utils/nonFarsiContentHint';
import {
  isLessonNotPassedHintSuppressed,
  setLessonNotPassedHintSuppressed
} from '../../utils/lessonNotPassedHint';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { lessonHeroEyebrow, resolveLessonHeroNumber } from '../../utils/lessonHeroLabel';
import {
  isLessonMarkedPassed,
  isQuizResultPassed,
  patchLessonExerciseCompleted,
  patchLessonPassed,
  patchLessonQuizPassed,
  patchLessonReviewed
} from '../../utils/patchLessonProgress';
import { isContentMissingError } from '../../utils/isContentMissingError';
import { LessonPracticeQuizExpandables } from './LessonPracticeQuizExpandables';
import { PassedLessonBanner } from './PassedLessonBanner';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';
import {
  learnerImageCarouselLogicalIndex,
  learnerImageCarouselNextButtonSide,
  learnerImageCarouselPrevButtonSide,
  learnerImageCarouselSwipeAction,
  learnerImageCarouselTrackTransform,
  learnerImageCarouselTrackUrls,
} from '../../utils/learnerImageCarouselMotion';
import { formatImageCarouselCounter } from '../../utils/imageCarouselLabels';
import { InlineLessonExerciseBlock } from './InlineLessonExerciseBlock';
import { VocabularyBlockDisplay } from './VocabularyBlockDisplay';
import { LessonMapPanel } from './LessonMapPanel';
import { buildCourseLessonMap } from '../../utils/lessonMapSections';
import type { LessonMapItem } from '../../utils/lessonMapSections';
import { LessonPageHero } from './LessonPageHero';
import { SimplePageHeader } from '../shared/SimplePageHeader';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';

interface LessonViewerProps {
  lessonId?: number;
  onBack?: () => void;
  onBackToDashboard?: () => void;
  onComplete?: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
  /** When set after navigation (e.g. global search), scroll to this content block once the lesson is loaded. */
  scrollToContentBlockId?: number;
  onScrollToBlockConsumed?: () => void;
  /** Open this lesson exercise (e.g. global search hit) once the lesson is loaded. */
  openExerciseId?: number;
  onOpenExerciseConsumed?: () => void;
  /** Open the lesson quiz modal from search: intro first (timer starts only after Start). */
  openLessonQuizFromSearchId?: number;
  onOpenLessonQuizFromSearchConsumed?: () => void;
  /**
   * Substring to mark inside the matched block's title (and to flash the block
   * itself for ~3s). Title-only because block body / tip content is HTML and we
   * don't rewrite it. Drives the highlight only when paired with
   * `scrollToContentBlockId`.
   */
  highlightTerm?: string;
  onHighlightTermConsumed?: () => void;
  onLearningSearchScopeChange?: (scope: { courseId?: number; levelId?: number; lessonId?: number }) => void;
  /**
   * Called whenever a quiz / exercise modal opens or closes inside the lesson.
   * The global learner-search uses this to defer a navigation request when the
   * learner clicks a search hit while a modal is on screen – the queued
   * navigation runs as soon as `open` becomes `false`.
   */
  onModalOpenStateChange?: (open: boolean) => void;
  /**
   * Bumped by the parent (App.tsx) to ask the lesson viewer to close any
   * currently-open quiz / exercise modal. The global learner-search bumps
   * this when the learner clicks a search hit while a modal is on screen so
   * the queued navigation can run immediately instead of waiting for the
   * learner to dismiss the modal manually.
   */
  closeModalsRequestId?: number;
}

const HIGHLIGHT_DURATION_MS = 5000;

export const LessonViewer: React.FC<LessonViewerProps> = ({
  lessonId: propLessonId,
  onBack,
  onBackToDashboard,
  onComplete,
  onNavigateToLesson,
  scrollToContentBlockId,
  onScrollToBlockConsumed,
  openExerciseId,
  onOpenExerciseConsumed,
  openLessonQuizFromSearchId,
  onOpenLessonQuizFromSearchConsumed,
  highlightTerm,
  onHighlightTermConsumed,
  onLearningSearchScopeChange,
  onModalOpenStateChange,
  closeModalsRequestId
}) => {
  const lessonId = propLessonId;
  useRefreshStickyAudioMap();
  const { user } = useAuth();
  const { showError } = useNotifications();
  const { t } = useTranslation(['learning', 'common', 'notifications']);
  const { direction, locale } = useAppLocale();
  const { pickTitle, pickDescription } = useLocalizedContent();
  const isRtlLayout = direction === 'rtl';
  const { isDark } = useTheme();
  const lockedNavArrowClass = (size: string) => localeMirroredNavArrowClass(isRtlLayout, size);

  const [lesson, setLesson] = useState<LessonDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [quizResult, setQuizResult] = useState<EnhancedQuizResultDto | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState<ContentBlockDto[][]>([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [animationError, setAnimationError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteToggling, setFavoriteToggling] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<{ id: number; type: ExerciseType } | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [userPlanIds, setUserPlanIds] = useState<number[]>([]);
  const [courseDetail, setCourseDetail] = useState<CourseDetailDto | null>(null);
  const [courseMapLoading, setCourseMapLoading] = useState(false);
  const [courseMapError, setCourseMapError] = useState<string | null>(null);
  /** In-place lesson swap from the map: keep the map mounted and skip the full-page loader. */
  const [lessonSwitching, setLessonSwitching] = useState(false);
  const courseDetailRef = useRef<CourseDetailDto | null>(null);
  const hasDisplayedLessonRef = useRef(false);
  const animationContainerRef = useRef<HTMLDivElement>(null);
  const scrollTargetConsumedRef = useRef<number | null>(null);
  /** Page that owns the active search flash. Leaving it ends the flash for good. */
  const highlightPageIndexRef = useRef<number | null>(null);
  const quizSearchNavRef = useRef<string | null>(null);
  const [quizModalBrowseMode, setQuizModalBrowseMode] = useState(false);
  /**
   * Block id currently flashing as the global-search match. Set when we scroll to
   * the matched block; cleared after HIGHLIGHT_DURATION_MS so other navigations
   * (e.g. internal block scrolls) don't accidentally pulse anything.
   */
  const [highlightBlockId, setHighlightBlockId] = useState<number | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  /** Snapshot of `highlightTerm` while a highlight is active – stays stable for the
   *  3-second flash even if the parent prop is cleared earlier. */
  const [activeHighlightTerm, setActiveHighlightTerm] = useState<string | null>(null);

  useEffect(() => {
    scrollTargetConsumedRef.current = null;
    quizSearchNavRef.current = null;
    // Cancel any in-flight block flash – the new lesson starts fresh.
    if (highlightTimerRef.current != null) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    setHighlightBlockId(null);
    setActiveHighlightTerm(null);
  }, [lessonId]);

  // Cancel timer + flash on unmount so we don't update state on a dead component
  // and the next mount of the same lesson starts clean.
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (scrollToContentBlockId == null) {
      scrollTargetConsumedRef.current = null;
    }
  }, [scrollToContentBlockId]);

  // Tell the app whether a quiz / exercise modal is currently obscuring the
  // lesson. The global learner search uses this to defer a navigation request
  // until the learner closes the open modal – clicking a search hit while a
  // modal is on screen would otherwise yank the lesson out from under it.
  const lastReportedModalOpenRef = useRef(false);
  useEffect(() => {
    const open = showQuizModal || selectedExercise != null;
    if (open === lastReportedModalOpenRef.current) return;
    lastReportedModalOpenRef.current = open;
    onModalOpenStateChange?.(open);
  }, [showQuizModal, selectedExercise, onModalOpenStateChange]);

  // On unmount, signal "modal closed" so any deferred navigation runs.
  useEffect(() => {
    return () => {
      if (lastReportedModalOpenRef.current) {
        lastReportedModalOpenRef.current = false;
        onModalOpenStateChange?.(false);
      }
    };
  }, [onModalOpenStateChange]);

  // Parent-driven close: when `closeModalsRequestId` increments, dismiss any
  // open quiz / exercise modal so the global learner-search's queued
  // navigation can fire without the learner having to dismiss the modal first.
  // We skip the initial render (closeModalsRequestId === undefined / 0) so we
  // don't clobber a modal that just opened from a previous search hit.
  const lastCloseRequestIdRef = useRef<number | undefined>(closeModalsRequestId);
  useEffect(() => {
    if (closeModalsRequestId === lastCloseRequestIdRef.current) return;
    lastCloseRequestIdRef.current = closeModalsRequestId;
    setShowQuizModal(false);
    setQuizModalBrowseMode(false);
    setSelectedExercise(null);
  }, [closeModalsRequestId]);

  useEffect(() => {
    if (!lesson || !onLearningSearchScopeChange) return;
    onLearningSearchScopeChange({
      courseId: lesson.courseId,
      levelId: lesson.levelId,
      lessonId: lesson.id
    });
  }, [lesson?.id, lesson?.courseId, lesson?.levelId, onLearningSearchScopeChange]);

  useEffect(() => {
    if (!openLessonQuizFromSearchId || openLessonQuizFromSearchId <= 0) {
      quizSearchNavRef.current = null;
      return;
    }
    if (!lesson) return;
    if (!lesson.lessonQuiz || lesson.lessonQuiz.id !== openLessonQuizFromSearchId) {
      onOpenLessonQuizFromSearchConsumed?.();
      return;
    }
    const key = `${lesson.id}:${openLessonQuizFromSearchId}`;
    if (quizSearchNavRef.current === key) return;
    quizSearchNavRef.current = key;
    setShowQuizModal(true);
    setQuizModalBrowseMode(true);
    onOpenLessonQuizFromSearchConsumed?.();
  }, [lesson, openLessonQuizFromSearchId, onOpenLessonQuizFromSearchConsumed]);

  useEffect(() => {
    if (loading || !lesson || pages.length === 0) return;
    const target = scrollToContentBlockId;
    if (target == null) return;
    // Snapshot the term once per effect run; the inline closure inside the rAF
    // would otherwise see whatever the prop is when rAF fires.
    const termToHighlight = highlightTerm?.trim();

    /** Flash the matched block once. The parent request is consumed immediately so a later visit does not replay it. */
    const triggerBlockHighlight = (blockId: number, pageIdx: number) => {
      if (!termToHighlight || termToHighlight.length === 0) return;
      highlightPageIndexRef.current = pageIdx;
      setActiveHighlightTerm(termToHighlight);
      setHighlightBlockId(blockId);
      onHighlightTermConsumed?.();
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightBlockId(null);
        setActiveHighlightTerm(null);
        highlightPageIndexRef.current = null;
        highlightTimerRef.current = null;
      }, HIGHLIGHT_DURATION_MS);
    };

    if (scrollTargetConsumedRef.current === target) return;

    const pageIdx = pages.findIndex((page) => page.some((b) => b.id === target));
    if (pageIdx < 0) {
      scrollTargetConsumedRef.current = target;
      onScrollToBlockConsumed?.();
      return;
    }

    setCurrentPage(pageIdx);
    scrollTargetConsumedRef.current = target;
    const domId = `lesson-content-block-${target}`;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const el = document.getElementById(domId);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onScrollToBlockConsumed?.();
        triggerBlockHighlight(target, pageIdx);
      });
    });
  }, [
    loading,
    lesson,
    pages,
    scrollToContentBlockId,
    highlightTerm,
    onScrollToBlockConsumed,
    onHighlightTermConsumed
  ]);

  useEffect(() => {
    if (highlightBlockId == null || highlightPageIndexRef.current == null) return;
    if (currentPage === highlightPageIndexRef.current) return;
    if (highlightTimerRef.current != null) {
      window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    highlightPageIndexRef.current = null;
    setHighlightBlockId(null);
    setActiveHighlightTerm(null);
  }, [currentPage, highlightBlockId]);

  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        const [plans, status] = await Promise.all([
          subscriptionService.getPlans(),
          subscriptionService.getSubscriptionStatus().catch(() => null)
        ]);
        setSubscriptionPlans(plans);
        const planId = status?.currentSubscription?.subscriptionPlan?.id;
        setUserPlanIds(planId != null ? [planId] : []);
      } catch (error) {
        console.error('LessonViewer: Failed to load subscription plans:', error);
      }
    };
    if (lessonId) loadSubscriptionData();
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId || !lesson) return;
    saveLastLessonPosition(lessonId, {
      lessonTitle: lesson.title,
      lessonTitleFa: lesson.titleFa,
      courseId: lesson.courseId,
      levelId: lesson.levelId,
      courseTitle: lesson.courseTitle,
      courseTitleFa: lesson.courseTitleFa,
      levelTitle: lesson.levelTitle,
      levelTitleFa: lesson.levelTitleFa,
      isDesignedForNonFarsiUsers: lesson.isDesignedForNonFarsiUsers
    });
  }, [
    lessonId,
    lesson
  ]);

  // Listen for event to open comment modal when navigating from UserCommentsModal
  useEffect(() => {
    const handleOpenComments = (event: CustomEvent) => {
      if (event.detail?.lessonId === lessonId) {
        setShowCommentModal(true);
      }
    };

    window.addEventListener('openLessonComments', handleOpenComments as EventListener);
    return () => {
      window.removeEventListener('openLessonComments', handleOpenComments as EventListener);
    };
  }, [lessonId]);

  type RefreshLessonOptions = {
    signal?: AbortSignal;
    /** Keep the learner on the same lesson page after reload (journey actions). */
    preservePage?: boolean;
    /** Skip full-screen loader (journey / progress tweaks). */
    silent?: boolean;
    /** Applied to fresh API data before updating state (quiz/exercise pass). */
    merge?: (lesson: LessonDetailDto) => LessonDetailDto;
  };

  const loadLesson = React.useCallback(async (options?: RefreshLessonOptions) => {
    const signal = options?.signal;
    const currentLessonId = lessonId;
    if (!currentLessonId) return;

    // Check session before loading lesson
    try {
      const { sessionInactivityTracker } = await import('../../utils/sessionInactivityTracker');
      sessionInactivityTracker.recordActivity();
    } catch (error) {
      console.warn('LessonViewer: Failed to record session activity:', error);
    }

    try {
      if (!options?.silent) setLoading(true);
      let lessonData = await contentService.getLessonDetail(currentLessonId, signal ? { signal } : undefined);
      if (options?.merge) {
        lessonData = options.merge(lessonData);
      }

      // Debug: Log text blocks to verify textFormatting is present
      const textBlocks = lessonData.contentBlocks.filter(b => b.type === ContentBlockType.Text);
      if (textBlocks.length > 0) {
        console.log('LessonViewer: Text blocks received:', textBlocks.map(b => ({
          id: b.id,
          hasTextContent: !!b.textContent,
          hasTextFormatting: !!b.textFormatting,
          textFormatting: b.textFormatting
        })));
      }
      
      setLesson(lessonData);
      hasDisplayedLessonRef.current = true;
      setIsFavorite(lessonData.isFavorite);
      if (!options?.silent) {
        setQuizResult(null);
      }
      
      // Split blocks by page separators
      const lessonPages: ContentBlockDto[][] = [];
      let currentPageBlocks: ContentBlockDto[] = [];
      
      const orderedBlocks = [...lessonData.contentBlocks].sort(
        (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      );
      orderedBlocks.forEach((block) => {
        if (block.type === ContentBlockType.PageSeparator) {
          if (currentPageBlocks.length > 0) {
            lessonPages.push(currentPageBlocks);
            currentPageBlocks = [];
          }
        } else {
          currentPageBlocks.push(block);
        }
      });
      
      // Add the last page if it has blocks
      if (currentPageBlocks.length > 0) {
        lessonPages.push(currentPageBlocks);
      }
      
      // If no separators, all blocks are on one page
      if (lessonPages.length === 0) {
        lessonPages.push(orderedBlocks.filter(b => b.type !== ContentBlockType.PageSeparator));
      }
      
      setPages(lessonPages);
      if (options?.preservePage) {
        setCurrentPage((prev) => Math.min(prev, Math.max(0, lessonPages.length - 1)));
      } else {
        setCurrentPage(0);
      }
      
      // Log lesson start action (not on silent progress refresh after quiz/exercise)
      if (user?.id && !options?.silent) {
        try {
          // Ensure logger is initialized
          userActionLogger.initialize();
          await userActionLogger.logUserAction({
            type: 'start_lesson',
            resourceType: 'lesson',
            resourceId: currentLessonId.toString(),
            userId: user.id,
            sessionId: userActionLogger['getSessionId']?.() || 'unknown',
            ipAddress: await userActionLogger['getClientIP']?.() || 'unknown',
            userAgent: navigator.userAgent,
            details: {
              lessonId: currentLessonId,
              lessonTitle: lessonData.title,
              lessonDescription: lessonData.description,
              timestamp: new Date().toISOString()
            }
          }, {
            component: 'LessonViewer',
            feature: 'learning',
            metadata: {
              url: window.location.href
            }
          });
        } catch (logError) {
          console.warn('LessonViewer: Failed to log lesson start:', logError);
        }
      }
    } catch (error) {
      // Don't show error for cancelled requests (navigation or Strict Mode cleanup)
      const isAborted = (error instanceof Error && error.name === 'AbortError') ||
        (error instanceof DOMException && error.name === 'AbortError');
      if (isAborted) return;
      if (isContentMissingError(error)) {
        showError(t('lesson.noLongerAvailable'));
        return;
      }
      const message = error instanceof Error ? error.message : t('lesson.loadFailed');
      showError(message);
    } finally {
      // Don't clear loading when request was aborted (new load may have started)
      if (!signal?.aborted) {
        setLoading(false);
        setLessonSwitching(false);
      }
    }
  }, [lessonId, user?.id]);

  const refreshLessonProgress = React.useCallback(
    async (patch?: (l: LessonDetailDto) => LessonDetailDto) => {
      if (patch) {
        setLesson((prev) => (prev ? patch(prev) : prev));
      }
      await loadLesson({ preservePage: true, silent: true, merge: patch });
    },
    [loadLesson]
  );

  const patchLesson = React.useCallback((patch: (l: LessonDetailDto) => LessonDetailDto) => {
    setLesson((prev) => (prev ? patch(prev) : prev));
  }, []);

  useEffect(() => {
    if (!lessonId) {
      setLesson(null);
      setLoading(false);
      return;
    }
    console.log('LessonViewer: lessonId changed to', lessonId);
    const controller = new AbortController();
    const switching = hasDisplayedLessonRef.current;
    setQuizResult(null);
    setShowQuizModal(false);
    setQuizModalBrowseMode(false);
    setShowCommentModal(false);
    setSelectedExercise(null);
    if (switching) {
      // Keep the open lesson map and the page shell. Only the lesson behind the map changes.
      setLessonSwitching(true);
    } else {
      setLesson(null);
      setLoading(true);
      setLessonSwitching(false);
      setCurrentPage(0);
      setPages([]);
    }
    loadLesson({ signal: controller.signal, silent: switching });
    return () => {
      controller.abort();
    };
  }, [lessonId, loadLesson]);

  // Check if animation file exists and add timeout fallback
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    // First check if file exists
    fetch('/animations/Locked_Icon.lottie', { method: 'HEAD' })
      .then(response => {
        if (!response.ok) {
          setAnimationError(true);
        } else {
          // If file exists, set a timeout to fallback if animation doesn't load
          timeoutId = setTimeout(() => {
            // Check if canvas inside the container is empty (animation didn't load)
            if (animationContainerRef.current) {
              const canvas = animationContainerRef.current.querySelector('canvas');
              if (canvas) {
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx && canvas.width > 0 && canvas.height > 0) {
                  try {
                    const imageData = ctx.getImageData(0, 0, Math.min(canvas.width, 100), Math.min(canvas.height, 100));
                    // Check if canvas is mostly empty (all pixels are transparent or same color)
                    const data = imageData.data;
                    let hasContent = false;
                    for (let i = 0; i < data.length; i += 4) {
                      const alpha = data[i + 3];
                      if (alpha > 0) {
                        hasContent = true;
                        break;
                      }
                    }
                    if (!hasContent) {
                      console.warn('Animation canvas appears empty, falling back to icon');
                      setAnimationError(true);
                    }
                  } catch (e) {
                    // If we can't read the canvas (CORS or other issue), assume it failed
                    console.warn('Cannot read animation canvas, falling back to icon');
                    setAnimationError(true);
                  }
                } else if (canvas.width === 0 || canvas.height === 0) {
                  // Canvas has no dimensions, animation likely didn't load
                  console.warn('Animation canvas has no dimensions, falling back to icon');
                  setAnimationError(true);
                }
              } else {
                // No canvas found, animation didn't render
                console.warn('No animation canvas found, falling back to icon');
                setAnimationError(true);
              }
            }
          }, 3000); // Wait 3 seconds for animation to load
        }
      })
      .catch(() => {
        setAnimationError(true);
      });
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const loadCourseForMap = useCallback(async (courseId: number, silent: boolean) => {
    if (!silent) {
      setCourseMapLoading(true);
      setCourseMapError(null);
    }
    try {
      const detail = await contentService.getCourseDetail(courseId);
      courseDetailRef.current = detail;
      setCourseDetail(detail);
      setCourseMapError(null);
    } catch (err) {
      if (!silent) {
        courseDetailRef.current = null;
        setCourseDetail(null);
        setCourseMapError(err instanceof Error ? err.message : 'Failed to load lesson map');
      }
    } finally {
      if (!silent) setCourseMapLoading(false);
    }
  }, []);

  useEffect(() => {
    const courseId = lesson?.courseId;
    if (!courseId) {
      if (!lessonSwitching) {
        courseDetailRef.current = null;
        setCourseDetail(null);
        setCourseMapError(null);
      }
      return;
    }
    const sameCourse = courseDetailRef.current?.id === courseId;
    void loadCourseForMap(courseId, sameCourse);
  }, [lesson?.courseId, lesson?.id, lesson?.isCompleted, lessonSwitching, loadCourseForMap]);

  const [homeDesign] = useLearnerHomeDesign();
  const animateLessonMapFocus = homeDesign !== 'mme';

  const lessonMapData = useMemo(() => {
    if (!courseDetail) {
      return { title: 'Lesson map', subtitle: undefined as string | undefined, sections: [] };
    }
    return buildCourseLessonMap(courseDetail, lessonId ?? lesson?.id ?? 0, locale);
  }, [courseDetail, lessonId, lesson?.id, locale]);

  const handleLessonMapNavigate = (item: LessonMapItem) => {
    if (!item.lessonId || item.lessonId === lessonId) return;
    if (onNavigateToLesson) {
      onNavigateToLesson(item.lessonId);
      return;
    }
    window.location.href = `/lesson/${item.lessonId}`;
  };

  const lessonMapPanel = lesson?.courseId ? (
    <LessonMapPanel
      mapData={lessonMapData}
      loading={courseMapLoading}
      error={courseMapError}
      onRetry={() => {
        if (lesson.courseId) void loadCourseForMap(lesson.courseId, false);
      }}
      onNavigate={handleLessonMapNavigate}
      persistOpenKey={`lesson-map-open:course:${lesson.courseId}`}
      subscriptionPlans={subscriptionPlans}
      toggleTourId="lesson-map-toggle"
      panelTourId="lesson-map-panel"
      {...(animateLessonMapFocus ? { animateFocusChange: true } : {})}
    />
  ) : null;

  if (loading && !lesson) {
    return (
      <PersianOverlayLoader isVisible message={t('lesson.loading')} />
    );
  }

  if (!lesson) {
    return (
      <div className="p-6 text-center max-w-6xl mx-auto">
        <p className="text-gray-600">{t('lesson.notFound')}</p>
        {onBack && (
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            {t('common:back')}
          </button>
        )}
      </div>
    );
  }

  if (lessonSwitching) {
    return (
      <>
        <div className="flex min-h-[50vh] items-center justify-center" aria-busy="true" aria-live="polite">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-300" aria-hidden />
        </div>
        {lessonMapPanel}
      </>
    );
  }

  if (lesson.id !== lessonId) {
    return (
      <>
        <div className="mx-auto max-w-6xl p-6 text-center">
          <p className="text-gray-600 dark:text-gray-300">{t('lesson.loadFailed')}</p>
        </div>
        {lessonMapPanel}
      </>
    );
  }

  // Only show locked view if this lesson matches the current lessonId
  // This prevents showing stale lesson data when navigating
  if (!lesson.isAccessible && lesson.id === lessonId) {
    const requiredPlans = lesson.requiredSubscriptionPlans || [];
    const kindLabel = t('contentLocked.kindLesson');

    return (
      <>
      <div className="p-6 max-w-6xl mx-auto">
        <div dir={direction} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          <div ref={animationContainerRef} className="w-48 h-48 mx-auto mb-4 flex items-center justify-center">
            {animationError ? (
              <Lock className="w-48 h-48 text-gray-400 dark:text-gray-500" />
            ) : (
              <DotLottieReact
                src="/animations/Locked_Icon.lottie"
                loop
                autoplay
                className="w-full h-full"
              />
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('contentLocked.headingLesson')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t('contentLocked.requiresPlan', { kind: kindLabel })}
          </p>
          {requiredPlans.length > 0 && (
            <div className="mb-6 text-start">
              <p className="text-gray-700 dark:text-gray-200 font-semibold mb-2">
                {t('contentLocked.requiredPlans', { count: requiredPlans.length })}
              </p>
              <div className="space-y-2">
                {requiredPlans.map((plan) => (
                  <div key={plan.id} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                    <p className="text-blue-900 dark:text-blue-200 font-medium">{plan.name}</p>
                    {plan.description && (
                      <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">{plan.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {requiredPlans.length > 0
              ? t('contentLocked.noPlanAccess', { count: requiredPlans.length, kind: kindLabel })
              : t('contentLocked.cannotAccess', { kind: kindLabel })}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                // Dispatch custom event to navigate to profile with subscription tab
                window.dispatchEvent(new CustomEvent('app:navigate', {
                  detail: { view: 'profile', hash: 'subscription' }
                }));
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('contentLocked.goToPlans')}
            </button>
            <div className="flex gap-3">
              {lesson.previousLessonId && (
                <button
                  onClick={() => {
                    const prevId = lesson.previousLessonId!;
                    if (onNavigateToLesson) {
                      onNavigateToLesson(prevId);
                    } else {
                      window.location.href = `/lesson/${prevId}`;
                    }
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className={lockedNavArrowClass('w-4 h-4')} />
                  {t('lesson.previousLesson')}
                </button>
              )}
              {lesson.nextLessonId && (
                <button
                  onClick={() => {
                    const nextId = lesson.nextLessonId!;
                    if (onNavigateToLesson) {
                      onNavigateToLesson(nextId);
                    } else {
                      window.location.href = `/lesson/${nextId}`;
                    }
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                >
                  {t('lesson.nextLesson')}
                  <ArrowRight className={lockedNavArrowClass('w-4 h-4')} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {lessonMapPanel}
      </>
    );
  }

  // Check if lesson has text blocks
  const hasTextBlocks = lesson.contentBlocks.some(block => block.type === ContentBlockType.Text);

  return (
    <>
    <LessonViewerContent 
      lesson={lesson}
      {...(courseDetail ? { courseDetail } : {})}
      pages={pages}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      showQuizModal={showQuizModal}
      setShowQuizModal={setShowQuizModal}
      quizModalBrowseMode={quizModalBrowseMode}
      setQuizModalBrowseMode={setQuizModalBrowseMode}
      {...(openExerciseId != null && openExerciseId > 0 ? { openExerciseId } : {})}
      {...(onOpenExerciseConsumed ? { onOpenExerciseConsumed } : {})}
      savingProgress={savingProgress}
      setSavingProgress={setSavingProgress}
      quizResult={quizResult}
      setQuizResult={setQuizResult}
      {...(onBack ? { onBack } : {})}
      {...(onBackToDashboard ? { onBackToDashboard } : {})}
      {...(onComplete ? { onComplete } : {})}
      {...(onNavigateToLesson ? { onNavigateToLesson } : {})}
      hasTextBlocks={hasTextBlocks}
      lessonId={lessonId || 0}
      loadLesson={loadLesson}
      refreshLessonProgress={refreshLessonProgress}
      patchLesson={patchLesson}
      showCommentModal={showCommentModal}
      setShowCommentModal={setShowCommentModal}
      isFavorite={isFavorite}
      setIsFavorite={setIsFavorite}
      favoriteToggling={favoriteToggling}
      setFavoriteToggling={setFavoriteToggling}
      selectedExercise={selectedExercise}
      setSelectedExercise={setSelectedExercise}
      subscriptionPlans={subscriptionPlans}
      userPlanIds={userPlanIds}
      highlightBlockId={highlightBlockId}
      activeHighlightTerm={activeHighlightTerm}
      {...(highlightTerm ? { quizExerciseHighlightTerm: highlightTerm } : {})}
      {...(onHighlightTermConsumed ? { onSearchHighlightConsumed: onHighlightTermConsumed } : {})}
    />
    {lessonMapPanel}
    </>
  );
};

// Inner component that uses the FontSizeContext
const LessonViewerContent: React.FC<{
  lesson: LessonDetailDto;
  /** Course catalog used for the lesson path. Loaded beside the lesson map so the map can stay mounted. */
  courseDetail?: CourseDetailDto | null;
  pages: ContentBlockDto[][];
  currentPage: number;
  setCurrentPage: (page: number) => void;
  showQuizModal: boolean;
  setShowQuizModal: (show: boolean) => void;
  quizModalBrowseMode: boolean;
  setQuizModalBrowseMode: (v: boolean) => void;
  openExerciseId?: number;
  onOpenExerciseConsumed?: () => void;
  savingProgress: boolean;
  setSavingProgress: (saving: boolean) => void;
  quizResult: EnhancedQuizResultDto | null;
  setQuizResult: (result: EnhancedQuizResultDto | null) => void;
  onBack?: () => void;
  onBackToDashboard?: () => void;
  onComplete?: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
  hasTextBlocks: boolean;
  lessonId: number;
  loadLesson: (options?: {
    preservePage?: boolean;
    silent?: boolean;
    merge?: (lesson: LessonDetailDto) => LessonDetailDto;
  }) => Promise<void>;
  refreshLessonProgress: (patch?: (l: LessonDetailDto) => LessonDetailDto) => Promise<void>;
  patchLesson: (patch: (l: LessonDetailDto) => LessonDetailDto) => void;
  showCommentModal: boolean;
  setShowCommentModal: (show: boolean) => void;
  isFavorite: boolean;
  setIsFavorite: (value: boolean) => void;
  favoriteToggling: boolean;
  setFavoriteToggling: (value: boolean) => void;
  selectedExercise: { id: number; type: ExerciseType } | null;
  setSelectedExercise: (v: { id: number; type: ExerciseType } | null) => void;
  subscriptionPlans: SubscriptionPlan[];
  userPlanIds: number[];
  /** Block id to flash with the global-search highlight ring (null when idle). */
  highlightBlockId: number | null;
  /** Term to mark inside that block's title while it's flashing. */
  activeHighlightTerm: string | null;
  /** Forwarded to QuizModal / ExerciseModal so they can flash the matched
   *  question and inline-mark the term. Undefined when the lesson was opened
   *  outside of the global learner search. */
  quizExerciseHighlightTerm?: string;
  onSearchHighlightConsumed?: () => void;
}> = ({
  lesson,
  courseDetail = null,
  pages,
  currentPage,
  setCurrentPage,
  showQuizModal,
  setShowQuizModal,
  quizModalBrowseMode,
  setQuizModalBrowseMode,
  openExerciseId,
  onOpenExerciseConsumed,
  savingProgress,
  setSavingProgress,
  quizResult,
  setQuizResult,
  onBack,
  onBackToDashboard,
  onComplete,
  onNavigateToLesson,
  hasTextBlocks,
  lessonId,
  loadLesson,
  refreshLessonProgress,
  patchLesson,
  showCommentModal,
  setShowCommentModal,
  isFavorite,
  setIsFavorite,
  favoriteToggling,
  setFavoriteToggling,
  selectedExercise,
  setSelectedExercise,
  subscriptionPlans,
  userPlanIds,
  highlightBlockId,
  activeHighlightTerm,
  quizExerciseHighlightTerm,
  onSearchHighlightConsumed
}) => {
  const { isDark } = useTheme();
  const { showError, showSuccess, showInfo } = useNotifications();
  const { t } = useTranslation(['learning', 'notifications']);
  const { direction, locale } = useAppLocale();
  const stickyAudioMap = React.useMemo(
    () => vocabularyToStickyMap(collectVocabularyItems(lesson.contentBlocks ?? [])),
    [lesson.contentBlocks]
  );
  const lessonForceEnglish = isDesignedForNonFarsiUsers(lesson);
  const { pickTitle: pickUiTitle, pickDescription: pickUiDescription } = useLocalizedContent();
  const isRtl = direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const {
    gateState: nonFarsiGateState,
    gateClick: gateNonFarsiClick,
    gateWithoutClick: gateNonFarsiWithoutClick,
    tryOpenPendingHint: tryOpenNonFarsiPendingHint,
    openHintManually: openNonFarsiHintManually,
    handleDismiss: handleNonFarsiGateDismiss,
    handleContinue: handleNonFarsiGateContinue,
    isRtl: nonFarsiGateRtl,
  } = useNonFarsiContentGate();
  const journeySectionRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { visibleBlockType } = useVisibleBlockTracker();
  const lessonTypography = useLessonTypographyPreferences();
  const [lockedExerciseModal, setLockedExerciseModal] = useState<{ exerciseTitle: string; requiredPlans: { id: number; name: string; description?: string }[] } | null>(null);
  const exerciseSearchNavRef = useRef<string | null>(null);
  const [lessonRatingAvg, setLessonRatingAvg] = useState<number | null>(null);
  const [lessonRatingCount, setLessonRatingCount] = useState(0);
  const [lessonRatingLoading, setLessonRatingLoading] = useState(true);
  const [activeQuizId, setActiveQuizId] = useState<number | null>(null);
  /** Quiz id for the open modal — stable while lesson progress patches re-render. */
  const quizModalTargetIdRef = useRef<number | null>(null);
  const activeExerciseIdRef = useRef<number | null>(null);
  const [markingReview, setMarkingReview] = useState(false);
  const autoReviewSentRef = useRef(false);
  const suppressAutoReviewRef = useRef(false);
  const autoPassSentRef = useRef(false);
  const suppressAutoPassRef = useRef(false);
  const [journeyHighlightPulse, setJourneyHighlightPulse] = useState(0);
  const [journeyCollapseRequest, setJourneyCollapseRequest] = useState(0);
  const [notPassedHintOpen, setNotPassedHintOpen] = useState(false);
  const pendingNextLessonIdRef = useRef<number | null>(null);
  const nextLessonButtonRef = useRef<HTMLButtonElement>(null);
  const prevLessonButtonRef = useRef<HTMLButtonElement>(null);
  const navArrowClass = (size: string) => localeMirroredNavArrowClass(isRtl, size);

  const autoReviewSuppressKey = `lesson-auto-review-suppressed:${lessonId}`;
  const autoPassSuppressKey = `lesson-auto-pass-suppressed:${lessonId}`;

  const { allContentViewed, markBlockViewed, trackBlocksOnSinglePage } =
    useLessonContentViewTracking(lessonId, pages, currentPage);

  const loadLessonRatingSummary = useCallback(async () => {
    if (!lessonId) {
      setLessonRatingAvg(null);
      setLessonRatingCount(0);
      setLessonRatingLoading(false);
      return;
    }
    setLessonRatingAvg(null);
    setLessonRatingCount(0);
    setLessonRatingLoading(true);
    try {
      const s = await commentService.getLessonRatingSummary(lessonId);
      const avg = ratingAverageFromSummary(s);
      if (s.ratingCount > 0 && avg != null) {
        setLessonRatingAvg(avg);
        setLessonRatingCount(s.ratingCount);
      } else {
        setLessonRatingAvg(null);
        setLessonRatingCount(0);
      }
    } catch {
      setLessonRatingAvg(null);
      setLessonRatingCount(0);
    } finally {
      setLessonRatingLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    void loadLessonRatingSummary();
  }, [loadLessonRatingSummary]);

  useEffect(() => {
    tryOpenNonFarsiPendingHint(lesson, 'lesson', lessonId);
  }, [lessonId, lesson, tryOpenNonFarsiPendingHint]);

  const activeQuizForceEnglish = useMemo(() => {
    if (activeQuizId == null) return false;
    const quiz = resolveLessonQuizzes(lesson).find((q) => q.id === activeQuizId);
    return isDesignedForNonFarsiUsers(quiz);
  }, [activeQuizId, lesson]);

  const activeExerciseForceEnglish = useMemo(() => {
    if (!selectedExercise) return false;
    const ex = lesson.exercises?.find((e) => e.id === selectedExercise.id);
    return isDesignedForNonFarsiUsers(ex);
  }, [selectedExercise, lesson.exercises]);

  const lessonHierarchyPathParts = useMemo(() => {
    const courseName = (
      courseDetail
        ? pickUiTitle(courseDetail)
        : pickUiTitle({ title: lesson.courseTitle, titleFa: lesson.courseTitleFa })
    )?.trim();
    const levelFromCourse = courseDetail?.levels?.find((level) => level.id === lesson.levelId);
    const levelName = (
      levelFromCourse
        ? pickUiTitle(levelFromCourse)
        : pickUiTitle({ title: lesson.levelTitle, titleFa: lesson.levelTitleFa })
    )?.trim();
    return [courseName, levelName].filter((part): part is string => Boolean(part));
  }, [courseDetail, lesson, pickUiTitle]);

  const openQuiz = (quizId: number) => {
    quizModalTargetIdRef.current = quizId;
    setActiveQuizId(quizId);
    setQuizModalBrowseMode(false);
    setShowQuizModal(true);
  };

  const openExerciseAfterAccess = async (ex: { id: number; title: string; subscriptionPlanIds?: number[] }) => {
    const requiredPlanIds = ex.subscriptionPlanIds ?? [];
    const hasRequiredPlan = requiredPlanIds.length === 0 || requiredPlanIds.some((id) => userPlanIds.includes(id));
    if (!hasRequiredPlan) {
      const requiredPlans = requiredPlanIds
        .map((id) => subscriptionPlans.find((p) => p.id === id))
        .filter((p): p is SubscriptionPlan => !!p)
        .map((p) => ({ id: p.id, name: p.name, ...(p.description != null && { description: p.description }) }));
      setLockedExerciseModal({ exerciseTitle: ex.title, requiredPlans });
      return;
    }
    try {
      await contentService.getLessonExerciseForLearner(ex.id);
      activeExerciseIdRef.current = ex.id;
      setSelectedExercise({ id: ex.id, type: 'lesson' });
    } catch (err) {
      const statusCode = typeof err === 'object' && err !== null && 'statusCode' in err ? (err as { statusCode: number }).statusCode : undefined;
      const msg = err instanceof Error ? err.message : '';
      const isNotAccessible =
        statusCode === 404 ||
        msg?.toLowerCase().includes('not accessible') ||
        msg?.toLowerCase().includes('not found');
      if (isNotAccessible) {
        const requiredPlans = requiredPlanIds
          .map((id) => subscriptionPlans.find((p) => p.id === id))
          .filter((p): p is SubscriptionPlan => !!p)
          .map((p) => ({ id: p.id, name: p.name, ...(p.description != null && { description: p.description }) }));
        setLockedExerciseModal({ exerciseTitle: ex.title, requiredPlans });
      } else {
        showError(msg || t('exercise.loadFailed'));
      }
    }
  };

  const nonFarsiLessonHintClick = () => openNonFarsiHintManually('lesson', lessonId);
  const showEnglishLockedHintIcon = locale !== 'en' && lessonForceEnglish;

  const handleStartExercise = async (
    ex: { id: number; title: string; subscriptionPlanIds?: number[] },
    clickEvent?: React.MouseEvent<HTMLElement>
  ) => {
    const summary = lesson.exercises?.find((e) => e.id === ex.id);
    const run = () => void openExerciseAfterAccess(ex);
    if (clickEvent) {
      gateNonFarsiClick(clickEvent, summary ?? ex, 'exercise', ex.id, run);
      return;
    }
    gateNonFarsiWithoutClick(journeySectionRef.current, summary ?? ex, 'exercise', ex.id, run);
  };

  const handleStartQuiz = (quizId: number, clickEvent?: React.MouseEvent<HTMLElement>) => {
    const quiz = resolveLessonQuizzes(lesson).find((q) => q.id === quizId);
    const run = () => openQuiz(quizId);
    if (clickEvent) {
      gateNonFarsiClick(clickEvent, quiz, 'quiz', quizId, run);
      return;
    }
    gateNonFarsiWithoutClick(journeySectionRef.current, quiz, 'quiz', quizId, run);
  };

  const handleStartExerciseRef = useRef(handleStartExercise);
  handleStartExerciseRef.current = handleStartExercise;

  useEffect(() => {
    exerciseSearchNavRef.current = null;
  }, [lessonId]);

  useEffect(() => {
    if (!openExerciseId || openExerciseId <= 0) {
      exerciseSearchNavRef.current = null;
      return;
    }
    if (lesson.id !== lessonId) return;
    const exercises = lesson.exercises ?? [];
    const ex = exercises.find((e) => e.id === openExerciseId);
    if (!ex) {
      onOpenExerciseConsumed?.();
      return;
    }
    const key = `${lesson.id}:${openExerciseId}`;
    if (exerciseSearchNavRef.current === key) return;
    exerciseSearchNavRef.current = key;

    void (async () => {
      try {
        await handleStartExerciseRef.current({
          id: ex.id,
          title: ex.title,
          subscriptionPlanIds: ex.subscriptionPlanIds ?? []
        });
      } finally {
        onOpenExerciseConsumed?.();
      }
    })();
  }, [lesson, lessonId, openExerciseId, onOpenExerciseConsumed]);

  useEffect(() => {
    suppressAutoReviewRef.current = sessionStorage.getItem(autoReviewSuppressKey) === '1';
    suppressAutoPassRef.current = sessionStorage.getItem(autoPassSuppressKey) === '1';
    autoReviewSentRef.current = false;
    autoPassSentRef.current = false;
  }, [lessonId, autoReviewSuppressKey, autoPassSuppressKey]);

  useEffect(() => {
    const first =
      lesson.lessonQuizzes?.[0]?.id ??
      lesson.lessonQuiz?.id ??
      null;
    setActiveQuizId(first);
    quizModalTargetIdRef.current = first;
  }, [lesson.id]);

  useEffect(() => {
    if (!lessonId || lesson.isLessonReviewed || pages.length === 0) return;
    if (!areMandatoryInlineExercisesComplete(lesson)) return;
    if (!allContentViewed) return;
    if (autoReviewSentRef.current) return;
    if (suppressAutoReviewRef.current || sessionStorage.getItem(autoReviewSuppressKey) === '1') return;
    autoReviewSentRef.current = true;
    void (async () => {
      try {
        await contentService.updateLessonProgress(lessonId, { lessonId, isFinished: true });
        await loadLesson({ preservePage: true, silent: true });
      } catch {
        autoReviewSentRef.current = false;
      }
    })();
  }, [
    allContentViewed,
    pages.length,
    lessonId,
    lesson.isLessonReviewed,
    lesson,
    loadLesson,
    autoReviewSuppressKey
  ]);

  useEffect(() => {
    if (!lessonId || lesson.isCompleted || pages.length === 0) return;
    if (!areLessonJourneyPassPrerequisitesMet(lesson)) return;
    if (autoPassSentRef.current) return;
    if (suppressAutoPassRef.current || sessionStorage.getItem(autoPassSuppressKey) === '1') return;
    autoPassSentRef.current = true;
    void (async () => {
      try {
        const passedPatch = (l: LessonDetailDto) => patchLessonPassed(l, true);
        patchLesson(passedPatch);
        const progressResult = await contentService.updateLessonProgress(lessonId, {
          lessonId,
          markPassedWithoutQuiz: true
        });
        notifyGamificationAfterAction({
          xpEarned: progressResult.xpAwarded ?? (progressResult as { XpAwarded?: number }).XpAwarded ?? 0
        });
        await refreshLessonProgress(passedPatch);
      } catch {
        autoPassSentRef.current = false;
        patchLesson((l) => patchLessonPassed(l, false));
        void refreshLessonProgress();
      }
    })();
  }, [
    lesson,
    lessonId,
    pages.length,
    patchLesson,
    refreshLessonProgress,
    autoPassSuppressKey
  ]);

  const handleMarkReviewed = async () => {
    if (!lessonId || lesson.isLessonReviewed) return;
    if (!areMandatoryInlineExercisesComplete(lesson)) {
      showInfo(t('lesson.completeExercisesFirst'));
      return;
    }
    const reviewedPatch = (l: LessonDetailDto) => patchLessonReviewed(l, true);
    try {
      setMarkingReview(true);
      suppressAutoReviewRef.current = false;
      sessionStorage.removeItem(autoReviewSuppressKey);
      autoReviewSentRef.current = true;
      patchLesson(reviewedPatch);
      await contentService.updateLessonProgress(lessonId, { lessonId, isFinished: true });
      showSuccess(t('lesson.markedReviewed'));
    } catch (error) {
      autoReviewSentRef.current = false;
      patchLesson((l) => patchLessonReviewed(l, false));
      showError(error instanceof Error ? error.message : t('lesson.updateProgressFailed'));
    } finally {
      setMarkingReview(false);
    }
  };

  const handleUnmarkReviewed = async () => {
    if (!lessonId || !lesson.isLessonReviewed) return;
    const clearedPatch = (l: LessonDetailDto) => patchLessonReviewed(l, false);
    try {
      setMarkingReview(true);
      suppressAutoReviewRef.current = true;
      sessionStorage.setItem(autoReviewSuppressKey, '1');
      autoReviewSentRef.current = true;
      patchLesson(clearedPatch);
      await contentService.updateLessonProgress(lessonId, { lessonId, isFinished: false });
      showSuccess(t('lesson.reviewCleared'));
    } catch (error) {
      suppressAutoReviewRef.current = false;
      sessionStorage.removeItem(autoReviewSuppressKey);
      patchLesson((l) => patchLessonReviewed(l, true));
      showError(error instanceof Error ? error.message : t('lesson.updateProgressFailed'));
    } finally {
      setMarkingReview(false);
    }
  };

  const handleUnmarkComplete = async () => {
    if (!lessonId || !(lesson.isCompleted && lesson.isLessonReviewed)) return;
    const unpassedPatch = (l: LessonDetailDto) => patchLessonPassed(l, false);
    try {
      setSavingProgress(true);
      suppressAutoPassRef.current = true;
      sessionStorage.setItem(autoPassSuppressKey, '1');
      autoPassSentRef.current = true;
      patchLesson(unpassedPatch);
      await contentService.updateLessonProgress(lessonId, { lessonId, unmarkPassed: true });
      showSuccess(t('lesson.unmarkedPassed'));
    } catch (error) {
      suppressAutoPassRef.current = false;
      sessionStorage.removeItem(autoPassSuppressKey);
      patchLesson((l) => patchLessonPassed(l, true));
      showError(error instanceof Error ? error.message : t('lesson.updateProgressFailed'));
    } finally {
      setSavingProgress(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!lessonId || !lesson) return;

    const blockers = getLessonJourneyBlockers(lesson);
    if (blockers.length > 0) {
      showInfo(t('lesson.completeStepsFirstDetail', { steps: blockers.join(' · ') }));
      return;
    }

    const passedPatch = (l: LessonDetailDto) => patchLessonPassed(l, true);
    try {
      setSavingProgress(true);
      suppressAutoPassRef.current = false;
      sessionStorage.removeItem(autoPassSuppressKey);
      autoPassSentRef.current = true;
      patchLesson(passedPatch);
      const progress: UpdateLessonProgressDto = {
        lessonId,
        markPassedWithoutQuiz: true
      };
      const progressResult = await contentService.updateLessonProgress(lessonId, progress);
      showSuccess(t('lesson.markedPassed'));
      notifyGamificationAfterAction({
        xpEarned: progressResult.xpAwarded ?? (progressResult as { XpAwarded?: number }).XpAwarded ?? 0
      });
    } catch (error) {
      autoPassSentRef.current = false;
      patchLesson((l) => patchLessonPassed(l, false));
      const message = error instanceof Error ? error.message : t('lesson.updateProgressFailed');
      showError(message);
    } finally {
      setSavingProgress(false);
    }
  };

  const journeySteps = buildLessonJourneySteps(lesson, {
    onMarkReviewed: () => void handleMarkReviewed(),
    onUnmarkReviewed: () => void handleUnmarkReviewed(),
    onOpenExercise: (id) => {
      const ex = lesson.exercises?.find((e) => e.id === id);
      if (ex) void handleStartExercise({ id: ex.id, title: ex.title, subscriptionPlanIds: ex.subscriptionPlanIds ?? [] });
    },
    onOpenQuiz: (quizId) => {
      handleStartQuiz(quizId);
    },
    onMarkPassed: () => void handleMarkComplete(),
    onUnmarkPassed: () => void handleUnmarkComplete(),
    markingReview,
    markingPassed: savingProgress,
    jt: (key, opts) => t(key, opts as Record<string, unknown>),
    locale,
  });

  const applyQuizPassedToLesson = (quizId: number, score?: number) => {
    patchLesson((l) => patchLessonQuizPassed(l, quizId, score));
  };

  const resolveOpenQuizId = () => quizModalTargetIdRef.current ?? activeQuizId;

  const handleQuizComplete = (result: EnhancedQuizResultDto) => {
    setQuizResult(result);
    const quizId = resolveOpenQuizId();
    if (isQuizResultPassed(result) && quizId != null) {
      applyQuizPassedToLesson(quizId, result.score);
    }
  };

  const handleQuizModalClose = () => {
    setShowQuizModal(false);
    setQuizModalBrowseMode(false);
  };

  const lessonMarkedPassed = isLessonMarkedPassed(lesson);
  /** Green “passed” hero/quiz styling — requires both passed and reviewed (normally set together). */
  const lessonPassed = lessonMarkedPassed && lesson.isLessonReviewed === true;
  const lessonRatingLabel = formatRatingOneDecimal(lessonRatingAvg);

  const navigateToLessonId = (targetId: number) => {
    if (onNavigateToLesson) {
      onNavigateToLesson(targetId);
    } else {
      window.location.href = `/lesson/${targetId}`;
    }
  };

  const neighborGateItem = (targetId: number) => {
    if (targetId === lesson.nextLessonId) {
      return { isDesignedForNonFarsiUsers: lesson.nextLessonIsDesignedForNonFarsiUsers };
    }
    if (targetId === lesson.previousLessonId) {
      return { isDesignedForNonFarsiUsers: lesson.previousLessonIsDesignedForNonFarsiUsers };
    }
    return { isDesignedForNonFarsiUsers: false };
  };

  const navigateToLessonWithEnglishGate = (targetId: number, anchorEl: HTMLElement | null) => {
    gateNonFarsiWithoutClick(
      anchorEl,
      neighborGateItem(targetId),
      'lesson',
      targetId,
      () => navigateToLessonId(targetId),
      { navigatesAway: true }
    );
  };

  const tryGoToNextLesson = () => {
    if (!lesson.nextLessonId) return;
    if (!lessonMarkedPassed && !isLessonNotPassedHintSuppressed()) {
      pendingNextLessonIdRef.current = lesson.nextLessonId;
      setNotPassedHintOpen(true);
      return;
    }
    navigateToLessonWithEnglishGate(lesson.nextLessonId, nextLessonButtonRef.current);
  };

  const tryGoToPreviousLesson = () => {
    if (!lesson.previousLessonId) return;
    navigateToLessonWithEnglishGate(lesson.previousLessonId, prevLessonButtonRef.current);
  };

  const handleNotPassedHintDismiss = () => {
    setNotPassedHintOpen(false);
    pendingNextLessonIdRef.current = null;
  };

  const handleNotPassedHintContinue = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      setLessonNotPassedHintSuppressed();
    }
    const nextId = pendingNextLessonIdRef.current ?? lesson.nextLessonId;
    setNotPassedHintOpen(false);
    pendingNextLessonIdRef.current = null;
    if (nextId) navigateToLessonWithEnglishGate(nextId, nextLessonButtonRef.current);
  };

  const handleNotPassedHintViewJourney = () => {
    setNotPassedHintOpen(false);
    pendingNextLessonIdRef.current = null;
    // Wait for the hint panel exit animation before scrolling/flashing the journey.
    window.setTimeout(() => {
      setJourneyHighlightPulse((n) => n + 1);
    }, 320);
  };

  useEffect(() => {
    const handleBeforePageTour = () => {
      setJourneyCollapseRequest((n) => n + 1);
    };

    window.addEventListener('app:beforeRequestPageTour', handleBeforePageTour);
    return () => window.removeEventListener('app:beforeRequestPageTour', handleBeforePageTour);
  }, []);

  useEffect(() => {
    setNotPassedHintOpen(false);
    pendingNextLessonIdRef.current = null;
  }, [lessonId]);

  const reviewableBlockViewedProp =
    trackBlocksOnSinglePage
      ? { onReviewableBlockViewed: markBlockViewed }
      : {};

  const lessonExercises = (lesson.exercises ?? []).filter((e) => !e.isInlineInLessonBody);
  const lessonQuizzes = resolveLessonQuizzes(lesson);
  const hasLessonPractice = lessonExercises.length > 0 || lessonQuizzes.length > 0;
  const practiceQuizPanels = hasLessonPractice ? (
    <div data-tour-id="lesson-practice">
      <LessonPracticeQuizExpandables
      exercises={lessonExercises}
      quizzes={lessonQuizzes}
      subscriptionPlans={subscriptionPlans}
      onStartExercise={(ex, e) => void handleStartExercise(ex, e)}
      onStartQuiz={(quizId, e) => handleStartQuiz(quizId, e)}
    />
    </div>
  ) : null;

  const handleHeroBack = onBack ?? (() => window.history.back());
  const lessonTitle = pickUiTitle(lesson);
  const lessonDescription = pickUiDescription(lesson)?.trim() ?? '';
  const lessonEyebrow = lessonHeroEyebrow(
    t,
    resolveLessonHeroNumber(lesson, courseDetail),
    locale
  );

  const favoriteButton = (
    <button
      type="button"
      data-tour-id="lesson-favorite"
      onClick={async () => {
        if (favoriteToggling || !lessonId) return;
        setFavoriteToggling(true);
        try {
          if (isFavorite) {
            await favoriteService.removeFavorite(lessonId);
            setIsFavorite(false);
            showSuccess(t('notifications:favorites.removed'));
          } else {
            await favoriteService.addFavorite(lessonId);
            setIsFavorite(true);
            showSuccess(t('notifications:favorites.added'));
          }
        } catch (e) {
          showError(e instanceof Error ? e.message : t('notifications:favorites.removeFailed'));
        } finally {
          setFavoriteToggling(false);
        }
      }}
      disabled={favoriteToggling}
      className="group inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-inherit outline-none transition-transform hover:scale-105 active:scale-95 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-rose-400/80 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
      style={{ WebkitTapHighlightColor: 'transparent' }}
      title={
        favoriteToggling
          ? 'Updating favorite'
          : isFavorite
            ? t('lesson.removeFromFavorites')
            : t('lesson.addToFavorites')
      }
      aria-label={
        favoriteToggling
          ? 'Updating favorite'
          : isFavorite
            ? t('lesson.removeFromFavorites')
            : t('lesson.addToFavorites')
      }
      aria-busy={favoriteToggling}
    >
      {favoriteToggling ? (
        <Loader2 className="pointer-events-none h-4 w-4 animate-spin text-rose-500 dark:text-rose-400" aria-hidden />
      ) : (
        <Heart
          className={
            isFavorite
              ? 'pointer-events-none h-4 w-4 fill-rose-500 text-rose-600 dark:fill-rose-500 dark:text-rose-400'
              : 'pointer-events-none h-4 w-4 text-gray-400 transition-colors group-hover:text-rose-500 dark:text-gray-500 dark:group-hover:text-rose-400'
          }
          aria-hidden
        />
      )}
    </button>
  );

  const passedBanner = lessonPassed ? (
    <PassedLessonBanner label={t('lesson.passedSuccessfullyBanner')} />
  ) : null;

  return (
    <WordHelpProvider blocks={lesson.contentBlocks ?? []} scopeKind="lesson" lessonId={lesson.id}>
    <div
      className="mme-tone px-3 py-4 sm:p-6 max-w-6xl mx-auto pb-8 space-y-4"
      data-lesson-tour-capabilities=""
      {...(hasLessonPractice ? { 'data-lesson-has-practice': 'true' } : {})}
    >
      {simpleHome ? (
        <SimplePageHeader
          tourId="lesson-header"
          eyebrow={lessonEyebrow}
          title={lessonTitle}
          onBack={handleHeroBack}
          backLabel={t('lesson.backToCourse')}
          above={
            <div className="flex items-center justify-between gap-2">
              <div data-tour-id="lesson-rating">
                <LearnerRatingCommentsTrigger
                  onOpenComments={() => setShowCommentModal(true)}
                  loading={lessonRatingLoading}
                  average={lessonRatingAvg}
                  ratingLabel={lessonRatingLabel}
                  ratingCount={lessonRatingCount}
                  accent="blue"
                />
              </div>
              <LearnerHeroViewFavoriteCluster
                viewCount={lesson.viewCount ?? 0}
                isRtl={isRtl}
                viewTourId="lesson-view"
              >
                {favoriteButton}
              </LearnerHeroViewFavoriteCluster>
            </div>
          }
        />
      ) : (
      <LessonPageHero
        tourId="lesson-header"
        compactChrome
        eyebrow={lessonEyebrow}
        title={lessonTitle}
        description={lessonDescription}
        compactDescription
        pathParts={lessonHierarchyPathParts}
        pathAriaLabel={t('lesson.hierarchyPathAria')}
        isPassed={lessonPassed}
        passedAriaLabel={t('lesson.passedAria')}
        topBar={
          <>
            <div data-tour-id="lesson-rating">
              <LearnerRatingCommentsTrigger
                onOpenComments={() => setShowCommentModal(true)}
                loading={lessonRatingLoading}
                average={lessonRatingAvg}
                ratingLabel={lessonRatingLabel}
                ratingCount={lessonRatingCount}
                accent="blue"
              />
            </div>
            <LearnerHeroViewFavoriteCluster
              viewCount={lesson.viewCount ?? 0}
              isRtl={isRtl}
              viewTourId="lesson-view"
            >
              {favoriteButton}
            </LearnerHeroViewFavoriteCluster>
          </>
        }
        belowIcon={
          <button
            type="button"
            onClick={handleHeroBack}
            className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
            aria-label={t('lesson.backToCourse')}
            title={t('lesson.backToCourse')}
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        }
        actionPlacement="underBack"
        underIconExtra={
          showEnglishLockedHintIcon ? (
            <EnglishLockedContentHintIcon
              onClick={nonFarsiLessonHintClick}
              dataTourId="lesson-english-locked"
            />
          ) : undefined
        }
        action={
          onBackToDashboard ? (
            <button
              type="button"
              onClick={onBackToDashboard}
              className="inline-flex h-5 w-8 items-center justify-center rounded-md border border-violet-200/90 bg-white/70 text-violet-950 shadow-sm backdrop-blur-[2px] transition-all hover:border-violet-300 hover:bg-white/90 active:scale-[0.98] dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-50 dark:hover:border-violet-600/60 dark:hover:bg-violet-950/55 md:w-9"
              aria-label={t('catalog.backToDashboard')}
              title={t('catalog.backToDashboard')}
            >
              <LayoutDashboard className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : undefined
        }
      />
      )}

      <div className="space-y-4">
        {passedBanner}
        <div ref={journeySectionRef}>
          <LessonJourneyTimeline
            key={lesson.id}
            steps={journeySteps}
            persistExpandLessonId={lessonId}
            highlightPulseRequest={journeyHighlightPulse}
            collapseRequest={journeyCollapseRequest}
            tourId="lesson-journey"
          />
        </div>
      </div>

      {/* Content Blocks with Page Navigation */}
      {pages.length > 1 ? (
        <>
          <div className="space-y-4" data-tour-id="lesson-content">
            {(() => {
              const blocks = pages[currentPage] ?? [];
              const blockTourIds = assignLessonBlockTourIds(blocks, locale, stickyAudioMap);
              return blocks.map((block, index) => {
                const isHighlighted = highlightBlockId != null && block.id === highlightBlockId;
                const tourIds = blockTourIds[index] ?? {};
                return (
                <ContentBlockDisplay
                  key={block.id || index}
                  block={block}
                  lessonId={lesson.id}
                  pinWordHelp={blocks.length <= 1}
                  isHighlighted={isHighlighted}
                  lessonTypography={lessonTypography}
                  lessonExercises={lesson.exercises ?? []}
                  userPlanIds={userPlanIds}
                  subscriptionPlans={subscriptionPlans}
                  onStartInlineExercise={(exerciseId, event) => {
                    const ex = lesson.exercises?.find((e) => e.id === exerciseId);
                    if (!ex) return;
                    void handleStartExercise(
                      {
                        id: ex.id,
                        title: ex.title,
                        subscriptionPlanIds: ex.subscriptionPlanIds ?? [],
                      },
                      event
                    );
                  }}
                  {...reviewableBlockViewedProp}
                  {...(isHighlighted && activeHighlightTerm ? { highlightTerm: activeHighlightTerm } : {})}
                  {...(tourIds.stickyAudioTourId ? { stickyAudioTourId: tourIds.stickyAudioTourId } : {})}
                  {...(tourIds.wordHelpTourId ? { wordHelpTourId: tourIds.wordHelpTourId } : {})}
                  {...(tourIds.wordHelpScopeTourId ? { wordHelpScopeTourId: tourIds.wordHelpScopeTourId } : {})}
                  {...(tourIds.tipTourId ? { tipTourId: tourIds.tipTourId } : {})}
                />
                );
              });
            })()}

            {currentPage === pages.length - 1 && practiceQuizPanels}
          </div>

          {/* Bottom section: page controller (same as tag viewer) */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/90 shadow-sm overflow-hidden">
            <div className="px-3 py-1.5">
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => currentPage > 0 && setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="p-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-200"
                  aria-label="Previous page"
                >
                  <ArrowLeft className={navArrowClass('w-4 h-4')} />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[4rem] text-center">
                  {currentPage + 1} / {pages.length}
                </span>
                <button
                  onClick={() => currentPage < pages.length - 1 && setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pages.length - 1}
                  className="p-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-200"
                  aria-label="Next page"
                >
                  <ArrowRight className={navArrowClass('w-4 h-4')} />
                </button>
              </div>
            </div>
          </section>
        </>
      ) : (
        <div className="space-y-4" data-tour-id="lesson-content">
          {(() => {
            const blocks = pages[0] ?? [];
            const blockTourIds = assignLessonBlockTourIds(blocks, locale, stickyAudioMap);
            return blocks.map((block, index) => {
              const isHighlighted = highlightBlockId != null && block.id === highlightBlockId;
              const tourIds = blockTourIds[index] ?? {};
              return (
              <ContentBlockDisplay
                key={block.id || index}
                block={block}
                lessonId={lesson.id}
                pinWordHelp={blocks.length <= 1}
                isHighlighted={isHighlighted}
                lessonTypography={lessonTypography}
                lessonExercises={lesson.exercises ?? []}
                userPlanIds={userPlanIds}
                subscriptionPlans={subscriptionPlans}
                onStartInlineExercise={(exerciseId, event) => {
                  const ex = lesson.exercises?.find((e) => e.id === exerciseId);
                  if (!ex) return;
                  void handleStartExercise(
                    {
                      id: ex.id,
                      title: ex.title,
                      subscriptionPlanIds: ex.subscriptionPlanIds ?? [],
                    },
                    event
                  );
                }}
                {...reviewableBlockViewedProp}
                {...(isHighlighted && activeHighlightTerm ? { highlightTerm: activeHighlightTerm } : {})}
                {...(tourIds.stickyAudioTourId ? { stickyAudioTourId: tourIds.stickyAudioTourId } : {})}
                {...(tourIds.wordHelpTourId ? { wordHelpTourId: tourIds.wordHelpTourId } : {})}
                {...(tourIds.wordHelpScopeTourId ? { wordHelpScopeTourId: tourIds.wordHelpScopeTourId } : {})}
                {...(tourIds.tipTourId ? { tipTourId: tourIds.tipTourId } : {})}
              />
              );
            });
          })()}

          {practiceQuizPanels}
        </div>
      )}

      {/* Bottom section: prev/next lesson navigation (only on last page) */}
      {currentPage === pages.length - 1 && (
        <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/90 shadow-sm overflow-hidden">
          <div className="p-4 sm:p-5 pt-4 pb-4">
            <div className="flex items-center justify-between w-full max-w-md mx-auto gap-3">
              <button
                ref={prevLessonButtonRef}
                onClick={tryGoToPreviousLesson}
                disabled={!lesson.previousLessonId}
                className="p-2 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-200 flex items-center gap-2"
                aria-label={t('lesson.previousLesson')}
              >
                <ArrowLeft className={navArrowClass('w-5 h-5')} />
                <span className="text-sm font-medium">{t('lesson.previousLesson')}</span>
              </button>
              <button
                ref={nextLessonButtonRef}
                onClick={tryGoToNextLesson}
                disabled={!lesson.nextLessonId}
                className="p-2 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-200 flex items-center gap-2"
                aria-label={t('lesson.nextLesson')}
              >
                <span className="text-sm font-medium">{t('lesson.nextLesson')}</span>
                <ArrowRight className={navArrowClass('w-5 h-5')} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Quiz Modal */}
      {showQuizModal && activeQuizId != null && (
        <QuizModal
          quizType="lesson"
          quizId={activeQuizId}
          contentForceEnglish={activeQuizForceEnglish}
          {...(lesson.previousQuizScore !== undefined ? { previousScore: lesson.previousQuizScore } : {})}
          browseMode={quizModalBrowseMode}
          {...(quizModalBrowseMode && quizExerciseHighlightTerm
            ? {
                searchHighlightTerm: quizExerciseHighlightTerm,
                ...(onSearchHighlightConsumed ? { onSearchHighlightConsumed } : {})
              }
            : {})}
          onComplete={handleQuizComplete}
          onClose={handleQuizModalClose}
          {...(locale !== 'en' && activeQuizForceEnglish && activeQuizId != null
            ? { onEnglishLockedHintClick: () => openNonFarsiHintManually('quiz', activeQuizId) }
            : {})}
        />
      )}

      {/* Exercise Modal */}
      {selectedExercise && (
        <ExerciseModal
          exerciseType={selectedExercise.type}
          exerciseId={selectedExercise.id}
          contentForceEnglish={activeExerciseForceEnglish}
          {...(quizExerciseHighlightTerm
            ? {
                searchHighlightTerm: quizExerciseHighlightTerm,
                ...(onSearchHighlightConsumed ? { onSearchHighlightConsumed } : {})
              }
            : {})}
          onClose={() => {
            activeExerciseIdRef.current = null;
            setSelectedExercise(null);
          }}
          {...(locale !== 'en' && activeExerciseForceEnglish
            ? {
                onEnglishLockedHintClick: () =>
                  openNonFarsiHintManually('exercise', activeExerciseIdRef.current ?? selectedExercise.id),
              }
            : {})}
          {...(selectedExercise.type === 'lesson'
            ? {
                onLessonExerciseCompleted: () => {
                  const exId = activeExerciseIdRef.current ?? selectedExercise.id;
                  if (exId != null) {
                    patchLesson((l) => patchLessonExerciseCompleted(l, exId));
                  }
                }
              }
            : {})}
        />
      )}

      <ExerciseLockedModal
        open={!!lockedExerciseModal}
        onClose={() => setLockedExerciseModal(null)}
        {...(lockedExerciseModal?.exerciseTitle
          ? { exerciseTitle: lockedExerciseModal.exerciseTitle }
          : {})}
        requiredPlans={lockedExerciseModal?.requiredPlans ?? []}
      />

      <LessonNotPassedModal
        open={notPassedHintOpen}
        isRtl={isRtl}
        anchorRef={nextLessonButtonRef}
        onDismiss={handleNotPassedHintDismiss}
        onContinue={handleNotPassedHintContinue}
        onViewJourney={handleNotPassedHintViewJourney}
      />

      <NonFarsiContentHintModal
        open={nonFarsiGateState.open}
        isRtl={nonFarsiGateRtl}
        anchorRect={nonFarsiGateState.anchorRect}
        contentKey={nonFarsiGateState.contentKey}
        onDismiss={handleNonFarsiGateDismiss}
        onContinue={handleNonFarsiGateContinue}
      />

      {/* Font Size Control is now in the BottomBanner (in App.tsx) */}

      {/* Comment Modal */}
      <CommentModal
        lessonId={lessonId}
        lessonTitle={pickUiTitle(lesson)}
        isOpen={showCommentModal}
        onClose={() => {
          setShowCommentModal(false);
          void loadLessonRatingSummary();
        }}
      />
    </div>
    </WordHelpProvider>
  );
};

interface ContentBlockDisplayProps {
  block: ContentBlockDto;
  lessonId: number;
  /** Lesson viewer: true. Tag browse: false so chart attributes time to tags only. */
  countsTowardLessons?: boolean;
  /** When true, the block container pulses with the search-result highlight ring. */
  isHighlighted?: boolean;
  /** Marked inside `block.title` while `isHighlighted` is true. */
  highlightTerm?: string;
  /** Learner prefs for Text / Text+Audio body only. */
  lessonTypography?: LessonTypographyScope;
  /** All lesson exercises (including inline) for subscription/completion metadata. */
  lessonExercises?: LessonExerciseSummaryDto[];
  userPlanIds?: number[];
  subscriptionPlans?: SubscriptionPlan[];
  /** Opens the lesson exercise modal for an inline exercise block. */
  onStartInlineExercise?: (exerciseId: number, event?: React.MouseEvent<HTMLButtonElement>) => void;
  /** Fired once when a non-exercise block becomes sufficiently visible (single-page lessons). */
  onReviewableBlockViewed?: (blockId: number) => void;
  stickyAudioTourId?: string;
  wordHelpTourId?: string;
  wordHelpScopeTourId?: string;
  tipTourId?: string;
  /** Merged tag lessons turn this off so the bar stays above its own title. */
  pinWordHelp?: boolean;
}

// Helper function to strip font-size from HTML content
const stripFontSizeFromHtml = (html: string): string => {
  if (!html) return html;
  // Create a temporary div to parse HTML and remove font-size more thoroughly
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Remove font-size from all elements
  const allElements = temp.querySelectorAll('*');
  allElements.forEach((el) => {
    if (el instanceof HTMLElement) {
      // Remove font-size from inline style
      if (el.style.fontSize) {
        el.style.fontSize = '';
      }
      // Remove from style attribute string
      const styleAttr = el.getAttribute('style');
      if (styleAttr) {
        const cleaned = styleAttr
          .split(';')
          .filter(prop => {
            const trimmed = prop.trim();
            return !trimmed.match(/^font-size\s*:/i) && !trimmed.match(/^fontSize\s*:/i);
          })
          .join(';')
          .trim();
        if (cleaned) {
          el.setAttribute('style', cleaned);
        } else {
          el.removeAttribute('style');
        }
      }
    }
  });
  
  return temp.innerHTML;
};

// Tip interface
interface Tip {
  id: string;
  description: string; // Plain text for search
  textRange?: { start: number; end: number };
  tipFormatting?: {
    fontFamily?: string;
    baseFontSize?: string;
    formattedContent?: string;
    textAlign?: 'left' | 'right' | 'justify' | 'justify-right';
  };
  // Legacy support: formatting field (will be migrated to tipFormatting)
  formatting?: {
    fontFamily?: string;
    baseFontSize?: string;
    formattedContent?: string;
    textAlign?: 'left' | 'right' | 'justify' | 'justify-right';
  };
}

const FLOATING_TIP_ANIM_MS = 200;

function tipPrefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// TipIcon component with resizable tooltip
const TipIcon: React.FC<{
  tip: Tip;
  position?: 'top' | 'bottom';
  dataTourId?: string;
}> = React.memo(({ tip, position = 'bottom', dataTourId }) => {
  const tipId = tip.id != null ? String(tip.id) : '';
  const markedOpen = isTextAudioTipMarkedOpen(tipId);
  const pendingOpenAnimation = isTextAudioTipPendingOpenAnimation(tipId);
  const [isOpen, setIsOpen] = React.useState(markedOpen);
  const [tooltipMounted, setTooltipMounted] = React.useState(markedOpen);
  /** Remount while already open: stay visible. Fresh open (incl. after remount): animate in. */
  const [tooltipVisible, setTooltipVisible] = React.useState(markedOpen && !pendingOpenAnimation);
  const [isResizing, setIsResizing] = React.useState(false);
  const [positionState, setPositionState] = React.useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = React.useState<{ width: number; height: number } | null>(null);
  const iconRef = React.useRef<HTMLButtonElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const resizeHandleRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();
  const resizeStartPos = React.useRef<{ width: number; height: number; x: number; y: number } | null>(null);
  const tipOpenedAtRef = React.useRef(0);

  const finishCloseTip = React.useCallback(() => {
    if (tipId) markTextAudioTipClosed(tipId);
    setIsOpen(false);
    setTooltipMounted(false);
    setTooltipVisible(false);
    setPositionState(null);
    setSize(null);
  }, [tipId]);

  const revealTooltip = React.useCallback(() => {
    setTooltipVisible(true);
    if (tipId) clearTextAudioTipPendingOpenAnimation(tipId);
  }, [tipId]);

  const closeTip = React.useCallback(() => {
    if (!tooltipMounted && !isOpen) return;
    if (tipPrefersReducedMotion()) {
      setIsOpen(false);
      setTooltipVisible(false);
      finishCloseTip();
      return;
    }
    setIsOpen(false);
    setTooltipVisible(false);
  }, [tooltipMounted, isOpen, finishCloseTip]);

  React.useLayoutEffect(() => {
    if (!tooltipMounted || !isOpen || tooltipVisible) return;
    if (tipPrefersReducedMotion()) {
      revealTooltip();
      return;
    }
    if (!positionState) return;

    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      if (cancelled) return;
      // Force layout so the closed state is painted before we add is-visible.
      void tooltipRef.current?.getBoundingClientRect();
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) revealTooltip();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [tooltipMounted, isOpen, tooltipVisible, positionState, revealTooltip]);

  const handleTooltipTransitionEnd = React.useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (!tooltipVisible && !isOpen) {
        finishCloseTip();
      }
    },
    [tooltipVisible, isOpen, finishCloseTip]
  );

  React.useEffect(() => {
    if (!tooltipMounted || tooltipVisible || isOpen) return;
    const timeoutId = window.setTimeout(finishCloseTip, FLOATING_TIP_ANIM_MS + 50);
    return () => window.clearTimeout(timeoutId);
  }, [tooltipMounted, tooltipVisible, isOpen, finishCloseTip]);

  React.useLayoutEffect(() => {
    if (!isOpen) return;
    if (!tipOpenedAtRef.current) {
      tipOpenedAtRef.current = performance.now();
    }
    if (!positionState && iconRef.current) {
      setPositionState(computeTipTooltipPosition(iconRef.current, size));
    }
  }, [isOpen, positionState, size]);
  React.useEffect(() => {
    if (isOpen && contentRef.current) {
      const removeInlineFontSize = () => {
        const allElements = contentRef.current?.querySelectorAll('*');
        allElements?.forEach((el) => {
          if (el instanceof HTMLElement) {
            if (el.style.fontSize) {
              el.style.fontSize = '';
            }
          }
        });
      };
      // Run immediately and after a short delay to catch any dynamic changes
      removeInlineFontSize();
      const timeout = setTimeout(removeInlineFontSize, 100);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, tip.tipFormatting?.formattedContent, tip.formatting?.formattedContent]);
  
  // Track viewport dimensions and recalculate on resize
  const [viewportDimensions, setViewportDimensions] = React.useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  }));

  // Recalculate viewport dimensions on window resize
  React.useEffect(() => {
    const handleResize = () => {
      setViewportDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tooltip dimensions are calculated dynamically when needed

  // Reset size when tooltip closes so it recalculates on next open
  React.useEffect(() => {
    if (!tooltipMounted) {
      setSize(null);
    }
  }, [tooltipMounted]);

  // Set size to 30% of viewport width and 10% of viewport height when tooltip opens
  React.useEffect(() => {
    if (isOpen && tooltipRef.current && !size) {
      // Use current viewport dimensions, not stale values
      const currentWidth = window.innerWidth * 0.3;
      const currentHeight = window.innerHeight * 0.1;
      setSize({
        width: currentWidth,
        height: currentHeight
      });
    }
  }, [isOpen, size]);

  // Handle resizing (mouse and touch)
  React.useEffect(() => {
    if (!isResizing) return;

    const applyResize = (clientX: number, clientY: number) => {
      if (!resizeStartPos.current || !tooltipRef.current) return;
      const deltaX = clientX - resizeStartPos.current.x;
      const deltaY = clientY - resizeStartPos.current.y;
      setSize(prev => {
        const currentWidth = prev?.width || window.innerWidth * 0.1;
        const currentHeight = prev?.height || window.innerHeight * 0.1;
        return {
          width: Math.max(120, currentWidth + deltaX),
          height: Math.max(60, currentHeight + deltaY)
        };
      });
      resizeStartPos.current = {
        ...resizeStartPos.current,
        x: clientX,
        y: clientY,
        width: (resizeStartPos.current.width || window.innerWidth * 0.1) + deltaX,
        height: (resizeStartPos.current.height || window.innerHeight * 0.1) + deltaY
      };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartPos.current || !tooltipRef.current) return;
      e.preventDefault();
      applyResize(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!resizeStartPos.current || !tooltipRef.current || !touch) return;
      e.preventDefault();
      applyResize(touch.clientX, touch.clientY);
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      resizeStartPos.current = null;
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
    document.addEventListener('touchcancel', handlePointerUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handlePointerUp);
      document.removeEventListener('touchcancel', handlePointerUp);
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Initial positioning - calculate position when tooltip opens
  // First calculate based on icon position only (for initial render), then refine once tooltip is rendered
  React.useEffect(() => {
    if (isOpen && iconRef.current) {
      const calculateInitialPosition = () => {
        const icon = iconRef.current;
        if (!icon) return;
        
        const rect = icon.getBoundingClientRect();
        const currentViewportWidth = window.innerWidth;
        const currentViewportHeight = window.innerHeight;
        
        // Use estimated tooltip dimensions for initial positioning
        const estimatedWidth = size?.width || (currentViewportWidth * 0.3);
        const estimatedHeight = size?.height || (currentViewportHeight * 0.1);
        
        // Calculate initial position - always position tooltip above icon
        const padding = 10;
        let x = rect.left + (rect.width / 2) - (estimatedWidth / 2);
        let y = rect.top - estimatedHeight - 10;
        
        // If there's not enough space above, position below instead
        if (y < padding) {
          y = rect.bottom + 10;
        }
        
        // Adjust horizontal position to keep tooltip within viewport
        if (x + estimatedWidth > currentViewportWidth - padding) {
          x = currentViewportWidth - estimatedWidth - padding;
        }
        if (x < padding) {
          x = padding;
        }
        
        // Adjust vertical position to keep tooltip within viewport
        if (y + estimatedHeight > currentViewportHeight - padding) {
          y = currentViewportHeight - estimatedHeight - padding;
        }
        if (y < padding) {
          y = padding;
        }
        
        // Set initial position - this will allow tooltip to render
        setPositionState({ x, y });
      };
      
      // Calculate initial position immediately
      calculateInitialPosition();
    }
  }, [isOpen, size]);

  // Refine position once tooltip is rendered and we have access to tooltipRef
  React.useEffect(() => {
    if (isOpen && tooltipRef.current && iconRef.current && positionState) {
      const refinePosition = () => {
        const icon = iconRef.current;
        const tooltip = tooltipRef.current;
        if (!icon || !tooltip) return;
        
        const rect = icon.getBoundingClientRect();
        const currentViewportWidth = window.innerWidth;
        const currentViewportHeight = window.innerHeight;
        
        // Get actual tooltip dimensions after it renders
        requestAnimationFrame(() => {
          const tooltipRect = tooltip.getBoundingClientRect();
          const tooltipWidth = tooltipRect.width > 0 ? tooltipRect.width : (size?.width || (currentViewportWidth * 0.3));
          const tooltipHeight = tooltipRect.height > 0 ? tooltipRect.height : (size?.height || (currentViewportHeight * 0.1));
          
          // Calculate refined position
          const padding = 10;
          let x = rect.left + (rect.width / 2) - (tooltipWidth / 2);
          let y = rect.top - tooltipHeight - 10;
          
          // If there's not enough space above, position below instead
          if (y < padding) {
            y = rect.bottom + 10;
          }
          
          // Adjust horizontal position to keep tooltip within viewport
          if (x + tooltipWidth > currentViewportWidth - padding) {
            x = currentViewportWidth - tooltipWidth - padding;
          }
          if (x < padding) {
            x = padding;
          }
          
          // Adjust vertical position to keep tooltip within viewport
          if (y + tooltipHeight > currentViewportHeight - padding) {
            y = currentViewportHeight - tooltipHeight - padding;
          }
          if (y < padding) {
            y = padding;
          }
          
          // Only update if position changed significantly (avoid infinite loops)
          if (positionState && (Math.abs(positionState.x - x) > 1 || Math.abs(positionState.y - y) > 1)) {
            setPositionState({ x, y });
          }
        });
      };
      
      // Refine position after tooltip renders
      const timeout1 = setTimeout(refinePosition, 10);
      const timeout2 = setTimeout(refinePosition, 50);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [isOpen, position, size, positionState]);

  // Clicking outside closes the tip. Scrolling keeps it on the icon until that icon leaves the view.
  React.useEffect(() => {
    if (!isOpen) return;

    let attached = false;

    const dismissIfOutside = (event?: MouseEvent) => {
      if (isWithinTipOpenGracePeriod(tipOpenedAtRef.current)) return;
      if (event) {
        const target = event.target as Node;
        if (iconRef.current?.contains(target)) return;
        if (tooltipRef.current?.contains(target)) return;
        if (resizeHandleRef.current?.contains(target)) return;
      }
      closeTip();
    };

    const handleClickOutside = (event: MouseEvent) => {
      dismissIfOutside(event);
    };

    const handleScroll = (event: Event) => {
      if (tooltipRef.current && event.target instanceof Node && tooltipRef.current.contains(event.target)) return;
      const icon = iconRef.current;
      if (!icon) return;
      if (isAnchorOutsideView(icon)) {
        if (!isWithinTipOpenGracePeriod(tipOpenedAtRef.current)) closeTip();
        return;
      }
      setPositionState(computeTipTooltipPosition(icon, size));
    };

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    const timeoutId = window.setTimeout(() => {
      attached = true;
      document.addEventListener('click', handleClickOutside, true);
    }, TEXT_AUDIO_TIP_OPEN_GRACE_MS);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      if (attached) {
        document.removeEventListener('click', handleClickOutside, true);
      }
    };
  }, [isOpen, closeTip, size]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeTip();
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    if (tooltipRef.current && size) {
      resizeStartPos.current = {
        width: size.width,
        height: size.height,
        x: e.clientX,
        y: e.clientY
      };
    }
  };

  const handleTouchResizeStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const t = e.touches[0];
    if (!t) return;
    e.preventDefault();
    setIsResizing(true);
    if (tooltipRef.current && size) {
      resizeStartPos.current = {
        width: size.width,
        height: size.height,
        x: t.clientX,
        y: t.clientY
      };
    }
  };

  // Render tooltip via portal to document.body to avoid positioning issues when rendered inside tip-icon-wrapper
  const showTooltip = tooltipMounted && positionState != null;
  const tooltipContent = showTooltip ? (
    <div
      ref={tooltipRef}
      className={`fixed z-[90] overflow-hidden rounded-lg shadow-2xl learner-tip-tooltip-panel${
        tooltipVisible ? ' is-visible' : ''
      }`}
      onTransitionEnd={handleTooltipTransitionEnd}
      style={{
        left: positionState.x,
        top: positionState.y,
        width: size?.width || window.innerWidth * 0.3,
        height: size?.height || window.innerHeight * 0.1,
        position: 'fixed',
        background: isDark 
          ? 'linear-gradient(135deg, rgba(55, 65, 81, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(237, 242, 247, 0.98) 0%, rgba(247, 250, 252, 0.98) 100%)',
        backdropFilter: 'blur(12px)',
        border: isDark 
          ? '1px solid rgba(75, 85, 99, 0.5)'
          : '1px solid rgba(229, 231, 235, 0.8)',
        boxShadow: isDark
          ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
          : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
    >
          {/* Header with close button */}
          <div className="relative h-4 flex items-center justify-end border-b border-yellow-300/20 dark:border-yellow-600/20 bg-yellow-50/30 dark:bg-yellow-900/10">
            <button
              onClick={handleClose}
              className="absolute top-0 right-0 p-0.5 m-0.5 rounded hover:bg-yellow-400/20 dark:hover:bg-yellow-600/30 text-yellow-600 dark:text-yellow-400 transition-colors"
              aria-label="Close tip"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="overflow-y-auto p-1.5" style={{ height: size ? `${(size.height || 0) - 16}px` : 'auto' }}>
            {(tip.tipFormatting || tip.formatting)?.formattedContent ? (
              <>
                <style dangerouslySetInnerHTML={{ __html: `
                  .tip-content-${tip.id} {
                    font-family: ${(tip.tipFormatting || tip.formatting)?.fontFamily || 'inherit'} !important;
                    font-size: 0.625rem !important;
                    font-weight: normal !important;
                    font-style: normal !important;
                    text-decoration: none !important;
                    color: #9ca3af !important;
                    line-height: 1.3 !important;
                  }
                  @media (prefers-color-scheme: dark) {
                    .tip-content-${tip.id} {
                      color: #d1d5db !important;
                    }
                  }
                  .dark .tip-content-${tip.id} {
                    color: #d1d5db !important;
                  }
                  .tip-content-${tip.id} * {
                    font-weight: inherit !important;
                    font-style: inherit !important;
                    text-decoration: inherit !important;
                    font-size: inherit !important;
                    color: inherit !important;
                  }
                  .tip-content-${tip.id} b,
                  .tip-content-${tip.id} strong {
                    font-weight: bold !important;
                  }
                  .tip-content-${tip.id} u {
                    text-decoration: underline !important;
                  }
                  .tip-content-${tip.id} i,
                  .tip-content-${tip.id} em {
                    font-style: italic !important;
                  }
                  .tip-content-${tip.id} font[face] {
                    display: inline;
                  }
                  .tip-content-${tip.id} span[style*="font-family"] {
                    display: inline;
                  }
                  .tip-content-${tip.id} span[style*="font-size"],
                  .tip-content-${tip.id} div[style*="font-size"],
                  .tip-content-${tip.id} p[style*="font-size"],
                  .tip-content-${tip.id} *[style*="font-size"],
                  .tip-content-${tip.id} span[style],
                  .tip-content-${tip.id} div[style],
                  .tip-content-${tip.id} p[style],
                  .tip-content-${tip.id} font[size] {
                    font-size: inherit !important;
                  }
                  .tip-content-${tip.id} [style*="font-size"] {
                    font-size: inherit !important;
                  }
                  .tip-content-${tip.id} div[data-align] {
                    display: block;
                    margin-bottom: 0.5em;
                  }
                  .tip-content-${tip.id} div[data-align="left"] {
                    text-align: left;
                    text-align-last: auto;
                    direction: ltr;
                  }
                  .tip-content-${tip.id} div[data-align="right"] {
                    text-align: right;
                    text-align-last: auto;
                    direction: ltr;
                  }
                  .tip-content-${tip.id} div[data-align="justify"] {
                    text-align: justify;
                    text-align-last: auto;
                    direction: ltr;
                  }
                  .tip-content-${tip.id} div[data-align="justify-right"] {
                    text-align: justify;
                    text-align-last: right;
                    direction: rtl;
                  }
                `}} />
                <div
                  ref={contentRef}
                  className={`tip-content-${tip.id} leading-tight whitespace-pre-wrap`}
                  style={{
                    lineHeight: '1.3',
                    fontSize: '0.625rem',
                    fontFamily: (tip.tipFormatting || tip.formatting)?.fontFamily || 'inherit',
                    color: '#9ca3af'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: stripFontSizeFromHtml((tip.tipFormatting || tip.formatting)?.formattedContent || '')
                  }}
                />
              </>
            ) : (
              <p 
                className="leading-tight text-gray-400 dark:text-gray-300 whitespace-pre-wrap" 
                style={{ 
                  lineHeight: '1.3',
                  fontFamily: (tip.tipFormatting || tip.formatting)?.fontFamily || 'inherit',
                  fontSize: '0.625rem',
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                  textDecoration: 'none'
                }}
              >
                {tip.description}
              </p>
            )}
          </div>
          
          {/* Resize handle (mouse + touch) */}
          <div
            ref={resizeHandleRef}
            onMouseDown={handleResizeStart}
            onTouchStart={handleTouchResizeStart}
            className="absolute bottom-0 right-0 w-6 h-6 sm:w-4 sm:h-4 cursor-nwse-resize touch-manipulation bg-gradient-to-br from-yellow-400/30 to-yellow-500/30 dark:from-yellow-600/40 dark:to-yellow-700/40"
            style={{
              clipPath: 'polygon(100% 0, 0 100%, 100% 100%)'
            }}
            role="button"
            aria-label="Resize tip window"
          />
    </div>
  ) : null;

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        data-tour-id={dataTourId}
        data-tip-target={tip.id != null ? String(tip.id) : undefined}
        onPointerDown={(e) => {
          e.stopPropagation();
          setTextAudioTipPointerActive(true);
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          setTextAudioTipPointerActive(false);
        }}
        onPointerCancel={(e) => {
          e.stopPropagation();
          setTextAudioTipPointerActive(false);
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (tooltipMounted) {
            closeTip();
            return;
          }
          tipOpenedAtRef.current = performance.now();
          if (tipId) {
            markTextAudioTipPendingOpenAnimation(tipId);
            markTextAudioTipOpen(tipId);
          }
          if (iconRef.current) {
            setPositionState(computeTipTooltipPosition(iconRef.current, size));
          }
          setIsOpen(true);
          setTooltipMounted(true);
          setTooltipVisible(false);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => dispatchPauseTextAudioForTip());
          });
        }}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-200 dark:bg-amber-600/70 text-amber-800 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-500/80 transition-all duration-200 cursor-pointer relative z-10 leading-none align-middle shrink-0 touch-manipulation ${
          tooltipVisible ? 'learner-tip-icon--active' : ''
        }`}
        aria-label="Show tip"
        style={{ position: 'relative', zIndex: 10 }}
      >
        <Lightbulb className="w-2.5 h-2.5" strokeWidth={2.25} />
      </button>
      {/* Render tooltip via portal to document.body to ensure correct positioning */}
      {tooltipContent && typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  );
});

TipIcon.displayName = 'TipIcon';

function blockHasAnyTips(tipsJson?: string): boolean {
  if (!tipsJson?.trim()) return false;
  try {
    const parsed = JSON.parse(tipsJson) as unknown;
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

function parseLessonTips(tipsJson?: string): Tip[] {
  if (!tipsJson) return [];
  try {
    const parsed = JSON.parse(tipsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((tip: Tip & { formatting?: Tip['formatting'] }) => {
      if (tip.formatting && !tip.tipFormatting) {
        return {
          ...tip,
          tipFormatting: tip.formatting,
          formatting: undefined,
        };
      }
      return tip;
    });
  } catch {
    return [];
  }
}

function assignLessonBlockTourIds(
  blocks: ContentBlockDto[],
  locale: string,
  globalStickyMap: StickyAudioMap = {},
  wordHelpTourKey = 'lesson-word-help'
): Array<{
  stickyAudioTourId?: string;
  tipTourId?: string;
  wordHelpTourId?: string;
  wordHelpScopeTourId?: string;
}> {
  let stickyAssigned = false;
  let tipAssigned = false;
  let wordHelpAssigned = false;
  return blocks.map((block) => {
    const blockLocale = pickBlockDisplayLocale(block, locale);
    const hasSticky = textBlockHasStickyAudioWords(globalStickyMap, {
      blockType: block.type,
      textContent: pickBlockTextContent(block, blockLocale),
      audioUrl: block.audioUrl,
      blockStickyJson: pickBlockStickyAudio(block),
      textFormatting: pickBlockTextFormatting(block, blockLocale),
    });
    const hasTips = blockHasAnyTips(pickBlockTips(block, blockLocale));
    const stickyAudioTourId = !stickyAssigned && hasSticky ? 'lesson-sticky-audio' : undefined;
    if (stickyAudioTourId) stickyAssigned = true;
    const tipTourId = !tipAssigned && hasTips ? 'lesson-tip' : undefined;
    if (tipTourId) tipAssigned = true;
    const isWordHelpBlock =
      block.type === ContentBlockType.Text || block.type === ContentBlockType.TextAudio;
    const wordHelpTourId = !wordHelpAssigned && isWordHelpBlock ? wordHelpTourKey : undefined;
    const wordHelpScopeTourId = wordHelpTourId ? `${wordHelpTourKey}-scope` : undefined;
    if (wordHelpTourId) wordHelpAssigned = true;
    return { stickyAudioTourId, tipTourId, wordHelpTourId, wordHelpScopeTourId };
  });
}

/** Same frame on every side: a full border plus a shadow that reaches the top edge. */
const lessonBlockFrameClass =
  'relative isolate bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-[0_0_6px_-1px_rgba(0,0,0,0.12),0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]';

// Image Carousel Component for learner view (separate component to manage state properly)
const ImageCarouselDisplay: React.FC<{
  blockRef: React.RefObject<HTMLDivElement>;
  blockDomId?: string;
  images: string[];
  title?: string;
  renderBlockHeader: (icon: React.ReactNode, defaultTitle: string) => React.ReactNode;
  blockTips?: Tip[];
  showHeaderRow?: boolean;
  /** Extra class on the outer wrapper – used for the global-search flash ring. */
  highlightClass?: string;
  tipTourId?: string;
}> = ({ blockRef, blockDomId, images, title, renderBlockHeader, blockTips = [], showHeaderRow = false, highlightClass = '', tipTourId }) => {
  const tourTipAnchorId =
    tipTourId && blockTips.length > 0 && blockTips[0].id != null ? String(blockTips[0].id) : null;
  const resolveTipTourId = (tip: Tip) =>
    tourTipAnchorId && String(tip.id) === tourTipAnchorId ? tipTourId : undefined;

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [transitionDirection, setTransitionDirection] = React.useState<'left' | 'right' | null>(null);
  const [imageAspectRatios, setImageAspectRatios] = React.useState<Array<number>>([]);
  const [containerHeight, setContainerHeight] = React.useState<number | null>(null);
  const [dragOffsetPx, setDragOffsetPx] = React.useState(0);
  const [isTouchDragging, setIsTouchDragging] = React.useState(false);
  const touchStartXRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { isDark: isDarkTheme } = useTheme();
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const overlayBtnClass =
    'absolute top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/60 text-white transition-all duration-200 opacity-60 hover:opacity-100 z-10';

  const resolveImageSrc = (imageSrc: string) =>
    imageSrc.startsWith('data:') || imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KG')
      ? imageSrc.startsWith('data:') ? imageSrc : `data:image/png;base64,${imageSrc}`
      : imageSrc;
  
  const recalcContainerHeight = React.useCallback(() => {
    const el = containerRef.current;
    if (!el || imageAspectRatios.length !== images.length || !imageAspectRatios.every((r) => r > 0)) {
      return;
    }
    const containerWidth = el.offsetWidth;
    const heights = imageAspectRatios.map((aspectRatio) => containerWidth / aspectRatio);
    setContainerHeight(Math.max(...heights));
  }, [imageAspectRatios, images.length]);

  React.useEffect(() => {
    recalcContainerHeight();
  }, [recalcContainerHeight]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => recalcContainerHeight());
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcContainerHeight]);
  
  const handleImageLoad = (idx: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    setImageAspectRatios(prev => {
      const newRatios = [...prev];
      newRatios[idx] = aspectRatio;
      return newRatios;
    });
  };
  
  const goToPrevious = () => {
    if (currentImageIndex <= 0) return;
    setTransitionDirection('right');
    setCurrentImageIndex((prev) => prev - 1);
  };

  const goToNext = () => {
    if (currentImageIndex >= images.length - 1) return;
    setTransitionDirection('left');
    setCurrentImageIndex((prev) => prev + 1);
  };
  
  const goToIndex = (index: number) => {
    setTransitionDirection(index > currentImageIndex ? 'left' : 'right');
    setCurrentImageIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    touchStartXRef.current = e.touches[0]!.clientX;
    setIsTouchDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartXRef.current == null || e.touches.length !== 1) return;
    const dx = e.touches[0]!.clientX - touchStartXRef.current;
    setDragOffsetPx(dx);
  };

  const handleTouchEnd = () => {
    const action = learnerImageCarouselSwipeAction(dragOffsetPx, isRtl);
    if (action === 'next') goToNext();
    else if (action === 'prev') goToPrevious();
    touchStartXRef.current = null;
    setDragOffsetPx(0);
    setIsTouchDragging(false);
  };

  const slidePercent = 100 / images.length;
  const trackImages = learnerImageCarouselTrackUrls(images, isRtl);
  const PrevIcon = isRtl ? ArrowRight : ArrowLeft;
  const NextIcon = isRtl ? ArrowLeft : ArrowRight;
  
  return (
    <div ref={blockRef} id={blockDomId} className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}>
      {showHeaderRow && (
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            {renderBlockHeader(<ImageIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />, 'Image')}
          </div>
          {blockTips.length > 0 && (
            <div className="flex items-center gap-1 ml-2">
              {blockTips.map((tip) => (
                <TipIcon
                  key={tip.id}
                  tip={tip}
                  position="bottom"
                  dataTourId={resolveTipTourId(tip)}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div className="relative">
        <div
          ref={containerRef}
          dir="ltr"
          className="relative overflow-hidden rounded-lg w-full touch-pan-y"
          style={containerHeight ? { height: `${containerHeight}px` } : { minHeight: '200px' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <div
            className={`flex h-full ${isTouchDragging ? '' : 'transition-transform duration-500 ease-in-out'}`}
            style={{
              transform: learnerImageCarouselTrackTransform(
                currentImageIndex,
                images.length,
                dragOffsetPx,
                isRtl,
              ),
              width: `${images.length * 100}%`
            }}
          >
            {trackImages.map((imageSrc, trackIdx) => {
              const logicalIdx = learnerImageCarouselLogicalIndex(trackIdx, images.length, isRtl);
              return (
              <div
                key={logicalIdx}
                className="flex h-full flex-shrink-0 items-center justify-center px-1 sm:px-2"
                style={{ width: `${slidePercent}%` }}
              >
                <LearnerExpandableImage
                  src={resolveImageSrc(imageSrc)}
                  alt={`${title || 'Lesson content'} - Image ${logicalIdx + 1}`}
                  fillContainer
                  className="h-full w-full rounded-lg object-contain shadow-sm"
                  onLoad={(e) => handleImageLoad(logicalIdx, e)}
                  onError={(e) => {
                    console.error('Failed to load image:', imageSrc.substring(0, 100));
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              );
            })}
          </div>
          
          {/* Overlay Navigation Buttons */}
          {currentImageIndex > 0 && (
            <button
              type="button"
              onClick={goToPrevious}
              className={`${learnerImageCarouselPrevButtonSide(isRtl)} ${overlayBtnClass}`}
              aria-label="Previous image"
            >
              <PrevIcon className="w-6 h-6" />
            </button>
          )}
          {currentImageIndex < images.length - 1 && (
            <button
              type="button"
              onClick={goToNext}
              className={`${learnerImageCarouselNextButtonSide(isRtl)} ${overlayBtnClass}`}
              aria-label="Next image"
            >
              <NextIcon className="w-6 h-6" />
            </button>
          )}
        </div>
        <div className="flex justify-center gap-1 mt-3">
          {images.map((_, imgIdx) => (
            <button
              key={imgIdx}
              onClick={() => goToIndex(imgIdx)}
              className={`w-2 h-2 rounded-full transition-all ${currentImageIndex === imgIdx ? (isDarkTheme ? 'bg-blue-400 w-6' : 'bg-blue-600 w-6') : (isDarkTheme ? 'bg-gray-600' : 'bg-gray-300')}`}
            />
          ))}
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">
          {formatImageCarouselCounter(currentImageIndex + 1, images.length)}
        </p>
      </div>
    </div>
  );
};

const ContentBlockDisplay: React.FC<ContentBlockDisplayProps> = ({
  block,
  lessonId,
  countsTowardLessons = true,
  isHighlighted = false,
  highlightTerm,
  lessonTypography,
  lessonExercises,
  userPlanIds = [],
  subscriptionPlans = [],
  onStartInlineExercise,
  onReviewableBlockViewed,
  stickyAudioTourId,
  wordHelpTourId,
  wordHelpScopeTourId,
  tipTourId,
  pinWordHelp = true,
}) => {
  // Don't display PageSeparator blocks - they're only for page organization
  if (block.type === ContentBlockType.PageSeparator) {
    return null;
  }

  const { locale: uiLocale, direction } = useAppLocale();
  const blockLocale = pickBlockDisplayLocale(block, uiLocale);
  const blockDomId = block.id != null ? `lesson-content-block-${block.id}` : undefined;
  // Search-result class applied to every block branch's outer container so the
  // pulse ring is consistent across Text / Audio / Image / Video / TextAudio.
  // `scroll-mt-24` keeps the matched block clear of the sticky lesson header.
  const highlightClass = isHighlighted ? 'search-result-highlight scroll-mt-24' : '';
  const titleTermActive = isHighlighted ? highlightTerm : undefined;
  const blockTitle = pickBlockTitle(block, blockLocale);
  const blockTextContent = pickBlockTextContent(block, blockLocale);
  const blockTextFormatting = pickBlockTextFormatting(block, blockLocale);
  const blockTipsJson = pickBlockTips(block, blockLocale);

  const blockRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const {
    enabled: stickyAudioEnabled,
    toggleBlock: toggleStickyAudio,
    effectiveMap: blockStickyAudioMap
  } = useStickyAudioBlockToggle(
    block.id,
    block.stickyAudio,
    blockTextContent,
    blockTextFormatting
  );

  const tipsSource = blockTipsJson;
  const tipsEarly = parseLessonTips(tipsSource);
  const tourTipAnchorId =
    tipTourId && tipsEarly.length > 0 && tipsEarly[0].id != null ? String(tipsEarly[0].id) : null;
  const resolveTipTourId = (tip: Tip) =>
    tourTipAnchorId && String(tip.id) === tourTipAnchorId ? tipTourId : undefined;

  const renderLearnerTipIcon = React.useCallback(
    (tip: Tip, position?: string) => (
      <TipIcon
        tip={tip}
        position={(position as 'top' | 'bottom') ?? 'bottom'}
        dataTourId={resolveTipTourId(tip)}
      />
    ),
    [tipTourId, tourTipAnchorId]
  );

  const renderStickyAudioToggle = (textLike: boolean) => {
    if (!textLike) return null;
    return (
      <div className={`${pinWordHelp ? 'word-help-sticky-row' : 'word-help-static-row'} flex w-full min-w-0 justify-start`}>
        <WordHelpBlockToggle
          blockId={block.id ?? 0}
          enabled={stickyAudioEnabled}
          onToggle={toggleStickyAudio}
          {...(stickyAudioTourId ? { dataTourId: stickyAudioTourId } : {})}
          {...(wordHelpTourId ? { wordHelpTourId } : {})}
          {...(wordHelpScopeTourId ? { scopeTourId: wordHelpScopeTourId } : {})}
        />
      </div>
    );
  };
  
  // Track block visibility for font size controls
  useBlockVisibility(block.type, blockRef);
  const startTimeRef = React.useRef<number | null>(null);
  const accumulatedTimeRef = React.useRef<number>(0);
  const submitIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Track visibility using IntersectionObserver
  React.useEffect(() => {
    if (!blockRef.current || !block.id) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setIsVisible(true);
            if (
              onReviewableBlockViewed &&
              block.id != null &&
              isReviewableLessonBlock(block)
            ) {
              onReviewableBlockViewed(block.id);
            }
            if (startTimeRef.current === null) {
              startTimeRef.current = Date.now();
            }
          } else {
            setIsVisible(false);
            if (startTimeRef.current !== null) {
              const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
              accumulatedTimeRef.current += timeSpent;
              startTimeRef.current = null;
              // Submit accumulated time when block becomes invisible
              if (accumulatedTimeRef.current > 0) {
                submitTimeTracking(accumulatedTimeRef.current);
                accumulatedTimeRef.current = 0;
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(blockRef.current);

    return () => {
      observer.disconnect();
      if (submitIntervalRef.current) {
        clearInterval(submitIntervalRef.current);
      }
      // Submit remaining time on unmount
      if (startTimeRef.current !== null) {
        const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
        accumulatedTimeRef.current += timeSpent;
      }
      if (accumulatedTimeRef.current > 0) {
        submitTimeTracking(accumulatedTimeRef.current);
      }
    };
  }, [block.id, block.type, lessonId, countsTowardLessons, onReviewableBlockViewed]);

  // Submit time tracking periodically (every 30 seconds) when visible
  React.useEffect(() => {
    if (!isVisible || !block.id) return;

    submitIntervalRef.current = setInterval(() => {
      if (startTimeRef.current !== null) {
        const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (timeSpent >= 30) {
          accumulatedTimeRef.current += timeSpent;
          submitTimeTracking(accumulatedTimeRef.current);
          accumulatedTimeRef.current = 0;
          startTimeRef.current = Date.now();
        }
      }
    }, 30000); // Check every 30 seconds

    return () => {
      if (submitIntervalRef.current) {
        clearInterval(submitIntervalRef.current);
      }
    };
  }, [isVisible, block.id, countsTowardLessons]);

  const submitTimeTracking = async (seconds: number) => {
    if (!block.id || seconds <= 0) return;
    try {
      await contentService.submitTimeTracking(block.id, {
        contentBlockId: block.id,
        secondsSpent: seconds,
        countsTowardLessons
      });
    } catch (error) {
      console.error('Failed to submit time tracking:', error);
    }
  };
  // Helper to render block header with optional title - only show if title exists
  const renderBlockHeader = (icon: React.ReactNode, defaultTitle: string) => {
    if (!blockTitle) return null;
    // Highlight the matched substring inside the block title when this block was
    // landed on from the global search. Body text / tip content is HTML and we
    // intentionally don't rewrite it – the pulse on the container is the cue.
    const titleNode = titleTermActive
      ? renderHighlightedText(blockTitle, titleTermActive)
      : blockTitle;
    return (
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <span className="font-semibold text-gray-700 dark:text-gray-300">
          {titleNode}
        </span>
      </div>
    );
  };

  // Parse tips from JSON
  const tips = tipsEarly;
  // Block tips are those without textRange or with empty/invalid textRange
  const blockTips = tips.filter(tip => {
    // If textRange doesn't exist at all, it's a block tip
    if (!tip.textRange) return true;
    // If textRange is null, it's a block tip
    if (tip.textRange === null) return true;
    // If textRange exists but is an empty object, it's a block tip
    if (typeof tip.textRange === 'object' && Object.keys(tip.textRange).length === 0) return true;
    // If textRange exists but doesn't have valid start/end, it's a block tip
    if (tip.textRange.start === undefined || tip.textRange.end === undefined) return true;
    // If start/end are null or invalid, it's a block tip
    if (tip.textRange.start === null || tip.textRange.end === null) return true;
    // If start/end are not numbers, it's a block tip
    if (typeof tip.textRange.start !== 'number' || typeof tip.textRange.end !== 'number') return true;
    // Otherwise it's a text range tip
    return false;
  });
  const textRangeTips = tips.filter(tip => {
    // Text range tips must have valid numeric start and end
    return tip.textRange && 
           typeof tip.textRange === 'object' &&
           typeof tip.textRange.start === 'number' && 
           typeof tip.textRange.end === 'number' &&
           tip.textRange.start !== null && 
           tip.textRange.end !== null;
  });

  if (block.type === ContentBlockType.Text) {
    const textContent = blockTextContent;
    const textFormattingJson = blockTextFormatting;

    let formatting;
    if (textFormattingJson) {
      try {
        if (typeof textFormattingJson === 'string') {
          formatting = JSON.parse(textFormattingJson);
        } else {
          formatting = textFormattingJson;
        }
      } catch (error) {
        console.error('Error parsing textFormatting:', error, textFormattingJson);
        formatting = undefined;
      }
    }

    return (
      <div
        ref={blockRef}
        id={blockDomId}
        className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}
      >
        {renderStickyAudioToggle(true)}
        {(blockTitle || blockTips.length > 0) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {renderBlockHeader(<FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />, 'Text Content')}
            </div>
            {blockTips.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {blockTips.map((tip) => (
                  <TipIcon
                    key={tip.id}
                    tip={tip}
                    position="bottom"
                    dataTourId={resolveTipTourId(tip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <FormattedTextBlock
          textContent={textContent}
          formatting={formatting}
          tips={textRangeTips}
          renderTipIcon={renderLearnerTipIcon}
          {...(lessonTypography ? { lessonTypography } : {})}
          {...(titleTermActive ? { searchHighlightTerm: titleTermActive } : {})}
          stickyAudioMap={blockStickyAudioMap}
          stickyAudioEnabled={stickyAudioEnabled}
          {...(block.id != null ? { wordHelpBlockId: block.id } : {})}
        />
      </div>
    );
  }

  if (block.type === ContentBlockType.Audio) {
    let audioSrc = block.audioUrl || '';
    const isInlineBase64 =
      audioSrc.startsWith('data:') ||
      audioSrc.startsWith('blob:') ||
      (!audioSrc.startsWith('/') && !audioSrc.startsWith('http') && audioSrc.length > 500);
    if (audioSrc && !isInlineBase64) {
      if (!audioSrc.startsWith('http') && !audioSrc.startsWith('/')) {
        audioSrc = `/uploads/audio/${audioSrc}`;
      }
      audioSrc = normalizeAppHostedUploadUrl(audioSrc);
    }
    
    return (
      <div ref={blockRef} id={blockDomId} className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}>
        {(blockTitle || blockTips.length > 0) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {renderBlockHeader(<Volume2 className="w-5 h-5 text-green-600 dark:text-green-400" />, 'Audio')}
            </div>
            {blockTips.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {blockTips.map((tip) => (
                  <TipIcon
                    key={tip.id}
                    tip={tip}
                    position="bottom"
                    dataTourId={resolveTipTourId(tip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {audioSrc && (
          <div className="min-w-0 max-w-full">
            <AudioPlayer
              src={audioSrc}
              className="dark:bg-gray-700 dark:border-gray-600"
              isDownloadable={block.isDownloadable || false}
            />
          </div>
        )}
      </div>
    );
  }

  if (block.type === ContentBlockType.TextAudio) {
    const textContent = blockTextContent;

    let formatting;
    if (blockTextFormatting) {
      try {
        if (typeof blockTextFormatting === 'string') {
          formatting = JSON.parse(blockTextFormatting);
        } else {
          formatting = blockTextFormatting;
        }
      } catch (error) {
        console.error('Error parsing textFormatting:', error);
        formatting = undefined;
      }
    }

    let audioSrc = block.audioUrl || '';
    const isInlineBase64 =
      audioSrc.startsWith('data:') ||
      audioSrc.startsWith('blob:') ||
      (!audioSrc.startsWith('/') && !audioSrc.startsWith('http') && audioSrc.length > 500);
    if (audioSrc && !isInlineBase64) {
      if (!audioSrc.startsWith('http') && !audioSrc.startsWith('/')) {
        audioSrc = `/uploads/audio/${audioSrc}`;
      }
      audioSrc = normalizeAppHostedUploadUrl(audioSrc);
    }

    return (
      <div
        ref={blockRef}
        id={blockDomId}
        className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}
      >
        {renderStickyAudioToggle(true)}
        {(blockTitle || blockTips.length > 0) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {renderBlockHeader(
                <><FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400 inline mr-1" />
                 <Volume2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 inline" /></>,
                'Text+Audio'
              )}
            </div>
            {blockTips.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {blockTips.map((tip) => (
                  <TipIcon
                    key={tip.id}
                    tip={tip}
                    position="bottom"
                    dataTourId={resolveTipTourId(tip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {audioSrc && textContent && (
          <TextAudioBlock
            textContent={textContent}
            textFormatting={formatting}
            audioUrl={audioSrc}
            {...(block.audioSyncData ? { audioSyncData: block.audioSyncData } : {})}
            tips={textRangeTips}
            isDownloadable={block.isDownloadable || false}
            renderTipIcon={renderLearnerTipIcon}
            {...(lessonTypography ? { lessonTypography } : {})}
            {...(titleTermActive ? { searchHighlightTerm: titleTermActive } : {})}
            stickyAudioMap={blockStickyAudioMap}
            stickyAudioEnabled={stickyAudioEnabled}
            {...(block.id != null ? { wordHelpBlockId: block.id } : {})}
          />
        )}
      </div>
    );
  }

  if (block.type === ContentBlockType.Image) {
    // Parse imageUrl (single string or JSON array)
    const parseImageUrls = (imageUrl: string | undefined): string[] => {
      if (!imageUrl) return [];
      try {
        if (imageUrl.startsWith('[')) {
          return JSON.parse(imageUrl);
        }
        return [imageUrl];
      } catch {
        return [imageUrl];
      }
    };
    
    // Get display mode from block metadata
    const getImageDisplayMode = (): 'stacked' | 'carousel' => {
      if (block.transcript && block.transcript.startsWith('{')) {
        try {
          const metadata = JSON.parse(block.transcript);
          return metadata.imageDisplayMode || 'stacked';
        } catch {
          return 'stacked';
        }
      }
      return 'stacked';
    };
    
    const imageUrls = parseImageUrls(block.imageUrl);
    const displayMode = getImageDisplayMode();
    
    const normalizedUrls = imageUrls.map(url => {
      const isBase64 = url.startsWith('data:image') || url.startsWith('/9j/') || url.startsWith('iVBORw0KG');
      if (isBase64 || url.startsWith('blob:')) return url;
      return normalizeAppHostedUploadUrl(url);
    });
    
    if (normalizedUrls.length === 0) {
      return (
        <div ref={blockRef} id={blockDomId} className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}>
          {(blockTitle || blockTips.length > 0) && (
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                {renderBlockHeader(<ImageIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />, 'Image')}
              </div>
              {blockTips.length > 0 && (
                <div className="flex items-center gap-1 ml-2">
                  {blockTips.map((tip) => (
                      <TipIcon
                        key={tip.id}
                        tip={tip}
                        position="bottom"
                        dataTourId={resolveTipTourId(tip)}
                      />
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-gray-500 text-sm">No image available</p>
        </div>
      );
    }
    
    // Carousel mode for multiple images - use separate component to avoid hooks violation
    if (normalizedUrls.length > 1 && displayMode === 'carousel') {
      return (
        <ImageCarouselDisplay
          blockRef={blockRef}
          {...(blockDomId ? { blockDomId } : {})}
          images={normalizedUrls}
          {...(blockTitle ? { title: blockTitle } : {})}
          renderBlockHeader={renderBlockHeader}
          blockTips={blockTips}
          showHeaderRow={!!(blockTitle || blockTips.length > 0)}
          {...(highlightClass ? { highlightClass } : {})}
          {...(tipTourId ? { tipTourId } : {})}
        />
      );
    }
    
    // Stacked mode (default) - show all images vertically
    return (
      <div ref={blockRef} id={blockDomId} className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}>
        {(blockTitle || blockTips.length > 0) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {renderBlockHeader(<ImageIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />, 'Image')}
            </div>
            {blockTips.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {blockTips.map((tip) => (
                  <TipIcon
                    key={tip.id}
                    tip={tip}
                    position="bottom"
                    dataTourId={resolveTipTourId(tip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        <div className="space-y-4">
            {normalizedUrls.map((imageSrc, imgIdx) => {
              const isBase64 = imageSrc.startsWith('data:image') || imageSrc.startsWith('/9j/') || imageSrc.startsWith('iVBORw0KG');
              const src = isBase64 && !imageSrc.startsWith('data:')
                ? `data:image/png;base64,${imageSrc}`
                : imageSrc;
              return (
                <div key={imgIdx} className="flex justify-center">
                  <LearnerExpandableImage
                    src={src}
                    alt={`${blockTitle || 'Lesson content'} - Image ${imgIdx + 1}`}
                    className="max-w-full h-auto rounded-lg shadow-sm"
                    onError={(e) => {
                      console.error('Failed to load image:', imageSrc.substring(0, 100));
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  if (block.type === ContentBlockType.Video) {
    let videoSrc = block.videoUrl || '';
    const isInlineBase64 =
      videoSrc.startsWith('data:') ||
      videoSrc.startsWith('blob:') ||
      (!videoSrc.startsWith('/') && !videoSrc.startsWith('http') && videoSrc.length > 500);
    if (videoSrc && !isInlineBase64) {
      if (!videoSrc.startsWith('http') && !videoSrc.startsWith('/')) {
        videoSrc = `/uploads/videos/${videoSrc}`;
      }
      videoSrc = normalizeAppHostedUploadUrl(videoSrc);
    }
    
    return (
      <div ref={blockRef} id={blockDomId} className={`${lessonBlockFrameClass} p-3 sm:p-6 ${highlightClass}`}>
        {(blockTitle || blockTips.length > 0) && (
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {renderBlockHeader(<Video className="w-5 h-5 text-purple-600 dark:text-purple-400" />, 'Video')}
            </div>
            {blockTips.length > 0 && (
              <div className="flex items-center gap-1 ml-2">
                {blockTips.map((tip) => (
                  <TipIcon
                    key={tip.id}
                    tip={tip}
                    position="bottom"
                    dataTourId={resolveTipTourId(tip)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {videoSrc && (
          <div className="flex justify-center">
            <video
              src={videoSrc}
              controls
              className="max-w-full h-auto rounded-lg shadow-sm"
              onError={(e) => {
                console.error('Failed to load video');
                (e.target as HTMLVideoElement).style.display = 'none';
              }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}
      </div>
    );
  }

  if (block.type === ContentBlockType.Exercise) {
    const exerciseId = block.lessonExerciseId;
    if (!exerciseId) {
      return (
        <div ref={blockRef} id={blockDomId} className={`rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200 ${highlightClass}`}>
          Exercise block is not linked to an exercise yet.
        </div>
      );
    }
    const summary = lessonExercises?.find((e) => e.id === exerciseId);
    return (
      <div ref={blockRef} id={blockDomId}>
        <InlineLessonExerciseBlock
          exerciseId={exerciseId}
          {...(blockTitle ? { blockTitle } : {})}
          {...(summary ? { exerciseSummary: summary } : {})}
          userPlanIds={userPlanIds}
          subscriptionPlans={subscriptionPlans}
          isHighlighted={isHighlighted}
          onOpen={(event) => onStartInlineExercise?.(exerciseId, event)}
        />
      </div>
    );
  }

  if (block.type === ContentBlockType.Vocabulary) {
    return (
      <div ref={blockRef} id={blockDomId} className={highlightClass}>
        <VocabularyBlockDisplay
          block={block}
          sourceKind="lesson"
          lessonId={lessonId}
        />
      </div>
    );
  }

  return null;
};

export default LessonViewer;
export { ContentBlockDisplay, TipIcon, assignLessonBlockTourIds };
export type { ContentBlockDisplayProps, Tip };

